import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../components/AppIcon';
import Button from '../components/ui/Button';
import Header from '../components/ui/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import FAQ from '../components/ui/FAQ';
import { homepageFAQs } from '../data/faqs';
import { scrollToElement } from '../utils/scrollUtils';

const Home = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [isLoadingBlogs, setIsLoadingBlogs] = useState(true);
  const [testimonialScrollPosition, setTestimonialScrollPosition] = useState(0);
  const [isTestimonialPaused, setIsTestimonialPaused] = useState(false);
  const navigate = useNavigate();

  // Project Calculator State
  const [calculatorState, setCalculatorState] = useState({
    projectType: 'Web Application',
    complexity: 'Medium',
    timeline: '2-3 months',
    additionalFeatures: []
  });

  // Helper functions for button actions
  const scrollToSection = async (sectionId) => {
    await scrollToElement(sectionId, { 
      smooth: true,
      maxRetries: 15,
      retryDelay: 150
    }).catch(() => {
      // Silent fail - scrollToElement handles retries internally
    });
  };

  const navigateToPage = (path) => {
    navigate(path);
  };


  const handleContactForm = () => {
    scrollToSection('contact');
  };


  const stats = [
    { number: '25+', label: 'Agile Enabled Employees' },
    { number: '10+', label: 'Delivered Projects' },
    { number: '3+', label: 'Years of Experience' },
    { number: '52L+', label: 'Lines Of Code Written' },
    { number: '9+', label: 'Satisfied Clients' },
    { number: '15+', label: 'Technologies Mastered' }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      jobTitle: 'CTO',
      company: 'TechFlow Solutions',
      companyDescription: '200 people chatbot service platform',
      rating: 5,
      description: 'OctalChip delivered an exceptional web application that exceeded our expectations. Their team was professional, responsive, and delivered the project on time. The quality of work and attention to detail was outstanding.',
      avatar: <Icon name="User" size={24} className="text-gray-600" />,
      companyLogo: 'TechFlow'
    },
    {
      name: 'Michael Chen',
      jobTitle: 'Product Manager',
      company: 'InnovateCorp',
      companyDescription: 'AI-powered innovation platform',
      rating: 4,
      description: 'Working with OctalChip was a game-changer for our business. They built a scalable mobile app that helped us increase user engagement by 300%. Their AI-driven development approach saved us time and money.',
      avatar: <Icon name="User" size={24} className="text-gray-600" />,
      companyLogo: 'InnovateCorp'
    },
    {
      name: 'Emily Rodriguez',
      jobTitle: 'CEO',
      company: 'StartupHub',
      companyDescription: 'The startup accelerator',
      rating: 5,
      description: 'OctalChip transformed our vision into reality with their cutting-edge development skills. The team was incredibly knowledgeable about the latest technologies and delivered a solution that perfectly fits our needs.',
      avatar: <Icon name="User" size={24} className="text-gray-600" />,
      companyLogo: 'StartupHub'
    },
    {
      name: 'David Thompson',
      jobTitle: 'Technical Director',
      company: 'Digital Dynamics',
      companyDescription: 'Digital transformation leader',
      rating: 4,
      description: 'The cloud infrastructure and DevOps solutions provided by OctalChip have been instrumental in our success. Their expertise in AWS and Kubernetes helped us scale efficiently and maintain high performance.',
      avatar: <Icon name="User" size={24} className="text-gray-600" />,
      companyLogo: 'Digital Dynamics'
    },
    {
      name: 'Lisa Wang',
      jobTitle: 'VP Engineering',
      company: 'CloudScale Inc',
      companyDescription: 'Enterprise cloud solutions',
      rating: 5,
      description: 'OctalChip\'s expertise in microservices architecture and cloud deployment helped us achieve 99.9% uptime. Their team\'s attention to detail and proactive communication made the entire process seamless.',
      avatar: <Icon name="User" size={24} className="text-gray-600" />,
      companyLogo: 'CloudScale'
    }
  ];

  // Infinite scroll animation with hover pause
  useEffect(() => {
    if (!isTestimonialPaused) {
      const interval = setInterval(() => {
        setTestimonialScrollPosition((prev) => {
          const cardWidth = 400; // Width of each testimonial card
          const totalWidth = testimonials.length * cardWidth; // Total width of all cards
          return prev >= totalWidth ? 0 : prev + 1; // Move 1px every frame for smooth animation
        });
      }, 16); // 60fps animation (1000ms / 60fps = ~16ms)

      return () => clearInterval(interval);
    }
  }, [testimonials.length, isTestimonialPaused]);

  // Load recent blogs
  useEffect(() => {
    const loadRecentBlogs = async () => {
      try {
        setIsLoadingBlogs(true);

        // Use Vite's import.meta.glob to dynamically import all blog files
        const blogModules = import.meta.glob('../blogs/*.jsx');
        const blogs = [];

        // Load all blog files dynamically
        for (const path in blogModules) {
          try {
            const module = await blogModules[path]();
            // Get the exported blog object (assuming each file exports one blog object)
            const blogData = Object.values(module)[0];
            if (blogData && blogData.slug && blogData.title) {
              blogs.push(blogData);
            } else {
              console.warn(`Invalid blog data in ${path}:`, blogData);
            }
          } catch (error) {
            console.error(`Error loading blog from ${path}:`, error);
          }
        }

        // Sort blogs by publish date (newest first) and take only the first 3
        blogs.sort((a, b) => new Date(b.publishDate) - new Date(a.publishDate));
        const recentThreeBlogs = blogs.slice(0, 3);

        setRecentBlogs(recentThreeBlogs);
      } catch (error) {
        console.error('Error loading recent blogs:', error);
        setRecentBlogs([]);
      } finally {
        setIsLoadingBlogs(false);
      }
    };

    loadRecentBlogs();
  }, []);

  // ScrollToTop component handles hash-based scrolling globally

  // Helper function to format dates
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Enhanced SEO Data
  const seoData = {
    title: 'OctalChip - Leading Web & Mobile App Development Company | AI-Powered Solutions',
    description: 'OctalChip is a premier web and mobile app development company offering AI-powered solutions, AI & ML services, custom software development, cloud services, and digital transformation. 25+ agile developers, 10+ delivered projects, 3+ years experience.',
    keywords: 'web development, mobile app development, software development, AI development, custom software, web applications, mobile applications, cloud services, DevOps, UI/UX design, digital transformation, technology solutions, software company, development agency, IT services, programming, coding, website development, app development, artificial intelligence, machine learning, cloud computing, AWS, Azure, GCP, React, Node.js, Python, JavaScript, TypeScript, agile development, scrum, project management, digital solutions, tech consulting, software consulting, enterprise software, SaaS development, e-commerce development, API development, database design, frontend development, backend development, full-stack development, responsive design, progressive web apps, native apps, cross-platform development, Flutter, React Native, iOS development, Android development',
    canonical: 'https://www.octalchip.com/',

    // GEO-specific data
    geoData: {
      region: "IN-GJ",
      placename: "Gujarat, India",
      latitude: "23.0225",
      longitude: "72.5714"
    },
    localBusinessData: true,
    serviceArea: [
      { "@type": "Country", "name": "India" },
      { "@type": "Country", "name": "Germany" },
      { "@type": "Country", "name": "Italy" },
      { "@type": "Country", "name": "Dubai" },
      { "@type": "Country", "name": "United Kingdom" },
      { "@type": "Country", "name": "Netherlands" }
    ],

    // AEO-specific data
    faqData: homepageFAQs,

    // Voice search optimization
    voiceSearchKeywords: [
      "web development company",
      "mobile app development services",
      "AI development solutions",
      "cloud services provider",
      "DevOps consulting",
      "software development agency",
      "digital transformation services",
      "custom software development"
    ],
    conversationalQueries: [
      "How can I find a good web development company?",
      "What is the cost of mobile app development?",
      "Who provides AI development services?",
      "Where can I get cloud services?",
      "How to choose a software development agency?"
    ],

    // Featured snippet optimization
    featuredSnippetContent: "OctalChip is a leading web and mobile app development company with 25+ agile developers, offering AI-powered solutions, AI & ML services, custom software development, cloud services, and digital transformation. We have delivered 10+ projects with 3+ years of experience.",

    // List data for featured snippets
    listData: {
      name: "OctalChip Development Services",
      description: "Comprehensive digital solutions offered by OctalChip",
      items: [
        {
          name: "Web Development",
          description: "Custom web applications built with React, Next.js, Vue.js, and Node.js. Responsive, scalable, and SEO-optimized websites",
          url: "https://www.octalchip.com/services/web-development"
        },
        {
          name: "Mobile App Development",
          description: "Native and cross-platform mobile applications for iOS and Android using React Native, Flutter, and native development",
          url: "https://www.octalchip.com/services/mobile-app-development"
        },
        {
          name: "AI Integration",
          description: "AI-powered solutions including machine learning models, chatbots, predictive analytics, and intelligent automation",
          url: "https://www.octalchip.com/services/ai-integration"
        },
        {
          name: "Cloud & DevOps",
          description: "Scalable cloud infrastructure setup, migration, and management with AWS, Azure, GCP, CI/CD pipelines, and monitoring",
          url: "https://www.octalchip.com/services/cloud-devops"
        },
        {
          name: "UI/UX Design",
          description: "User-centered design with user research, wireframing, prototyping, design systems, and usability testing",
          url: "https://www.octalchip.com/services/ui-ux-design"
        },
        {
          name: "Backend Development",
          description: "Robust backend systems with microservices architecture, APIs, database optimization, and real-time communication",
          url: "https://www.octalchip.com/services/backend-development"
        },
        {
          name: "Workflow Automation & Integrations",
          description: "Professional workflow automation services using n8n, Zapier, and Make to connect tools and automate business processes",
          url: "https://www.octalchip.com/services/workflow-automation"
        },
        {
          name: "AI & ML Services",
          description: "Comprehensive AI and Machine Learning solutions including ML development, NLP, computer vision, predictive analytics, chatbots, deep learning, and data science",
          url: "https://www.octalchip.com/ai-ml-services"
        }
      ]
    },

    // Table data for pricing featured snippets
    tableData: {
      name: "OctalChip Pricing Plans",
      description: "Development service pricing options",
      about: "Development Services Pricing",
      table: {
        "@type": "Table",
        "name": "Development Services Pricing",
        "about": "Pricing plans for different development services",
        "table": [
          ["Service Type", "Price", "Description"],
          ["Hourly Engagement", "$18/hour", "Pay-as-you-go development services"],
          ["Monthly Dedicated Dev", "$2560/month", "Full-time developer dedicated to your project"],
          ["Fixed-Cost Projects", "Custom Pricing", "Complete solution delivered within timeline"]
        ]
      }
    },

    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "OctalChip - Leading Web & Mobile App Development Company",
      "description": "OctalChip is a premier web and mobile app development company offering AI-powered solutions, AI & ML services, custom software development, cloud services, and digital transformation.",
      "url": "https://www.octalchip.com/",
      "mainEntity": {
        "@type": "Organization",
        "name": "OctalChip",
        "url": "https://www.octalchip.com",
        "logo": "https://www.octalchip.com/favicon.svg",
        "description": "Leading web and mobile app development company offering AI-powered solutions, AI & ML services, custom software development, cloud services, and digital transformation.",
        "foundingDate": "2021",
        "numberOfEmployees": "25",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "404, Opera point, kothariya",
          "addressLocality": "Gujarat",
          "addressRegion": "Gujarat",
          "addressCountry": "IN",
          "postalCode": "360022"
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "customer service",
          "email": "business@octalchip.com",
          "telephone": "+91-7574082582"
        },
        "sameAs": [
          "https://linkedin.com/company/octalchip",
          "https://twitter.com/octalchip",
          "https://github.com/octalchip",
          "https://www.facebook.com/octalchip",
          "https://www.instagram.com/octalchiptech/"
        ],
        "serviceArea": {
          "@type": "GeoCircle",
          "geoMidpoint": {
            "@type": "GeoCoordinates",
            "latitude": 23.0225,
            "longitude": 72.5714
          },
          "geoRadius": "50000"
        },
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Development Services",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Web Development",
                "description": "Custom web applications and websites"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Mobile App Development",
                "description": "iOS and Android mobile applications"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "AI Development",
                "description": "AI-powered solutions and machine learning"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Cloud Services",
                "description": "AWS, Azure, GCP cloud solutions"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "AI & ML Services",
                "description": "Comprehensive AI and Machine Learning solutions including ML, NLP, computer vision, predictive analytics, and data science"
              }
            }
          ]
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.9",
          "ratingCount": "150",
          "bestRating": "5",
          "worstRating": "1"
        }
      }
    }
  };

  const services = [
    {
      title: 'Web Development',
      description: 'Transform your digital presence with custom web applications built using cutting-edge technologies. We create responsive, scalable, and high-performance websites that drive business growth.',
      icon: 'Globe',
      path: '/services/web-development'
    },
    {
      title: 'Mobile App Development',
      description: 'Create powerful mobile applications for iOS and Android. We build native and cross-platform apps that deliver exceptional user experiences and drive business growth.',
      icon: 'Smartphone',
      path: '/services/mobile-app-development'
    },
    {
      title: 'AI Integration',
      description: 'Harness the power of artificial intelligence to transform your business. We build intelligent solutions that automate processes, enhance decision-making, and deliver exceptional user experiences.',
      icon: 'Cpu',
      path: '/services/ai-integration'
    },
    {
      title: 'Cloud & DevOps',
      description: 'Transform your infrastructure with scalable cloud solutions and automated DevOps practices. We help you build, deploy, and manage applications with speed, reliability, and efficiency.',
      icon: 'Cloud',
      path: '/services/cloud-devops'
    },
    {
      title: 'UI/UX Design',
      description: 'Create exceptional user experiences that delight users and drive business results. We combine user research, thoughtful design, and testing to build interfaces that work beautifully.',
      icon: 'LayoutTemplate',
      path: '/services/ui-ux-design'
    },
    {
      title: 'Backend Development',
      description: 'Build robust, scalable backend systems that power your applications. We create high-performance APIs, microservices, and databases that handle millions of requests with reliability and speed.',
      icon: 'Server',
      path: '/services/backend-development'
    },
    {
      title: 'Workflow Automation & Integrations',
      description: 'Design, build, and optimize automated workflows using n8n, Zapier, and Make to connect your tools, eliminate manual work, and improve operational efficiency.',
      icon: 'Settings',
      path: '/services/workflow-automation'
    }
  ];

  const automationServices = [
    {
      title: 'n8n Automation',
      description: 'Self-hosted workflow automation for full control, security, and enterprise scalability',
      icon: 'Settings',
      path: '/services/n8n-automation#hero',
      features: ['Self-hosted', 'Full Control', 'Enterprise Security', 'Scalable'],
      gradient: 'from-[#00d690] to-[#00b876]',
      chevronColor: 'border-t-[#00d690]',
      chevronHoverColor: 'group-hover:border-t-[#00b876]'
    },
    {
      title: 'Zapier Integrations',
      description: 'Fast and reliable integrations between popular SaaS tools for sales, marketing, and operations',
      icon: 'Zap',
      path: '/services/zapier-integration#hero',
      features: ['Fast Setup', 'Reliable', 'Popular Tools', 'Easy to Use'],
      gradient: 'from-[#00b876] to-[#00d690]',
      chevronColor: 'border-t-[#00b876]',
      chevronHoverColor: 'group-hover:border-t-[#00d690]'
    },
    {
      title: 'Make Automation',
      description: 'Advanced visual workflows with complex logic, routing, and data transformation capabilities',
      icon: 'Settings',
      path: '/services/make-automation#hero',
      features: ['Complex Logic', 'Visual Workflows', 'Data Transformation', 'Advanced Routing'],
      gradient: 'from-[#00d690] via-[#00b876] to-[#00d690]',
      chevronColor: 'border-t-[#00d690]',
      chevronHoverColor: 'group-hover:border-t-[#00b876]'
    }
  ];

  const aiMLServices = [
    {
      title: 'Machine Learning Development',
      description: 'Build custom ML models for prediction, classification, and pattern recognition. We develop scalable machine learning solutions tailored to your business needs.',
      icon: 'Brain',
      path: '/services/ai-ml-services/machine-learning'
    },
    {
      title: 'Natural Language Processing',
      description: 'Advanced NLP solutions for text analysis, sentiment analysis, language translation, and intelligent text processing. Transform unstructured text into actionable insights.',
      icon: 'FileText',
      path: '/services/ai-ml-services/natural-language-processing'
    },
    {
      title: 'Computer Vision',
      description: 'Image and video analysis solutions for object detection, recognition, classification, and content moderation. Leverage visual data to automate processes.',
      icon: 'Eye',
      path: '/services/ai-ml-services/computer-vision'
    },
    {
      title: 'Predictive Analytics',
      description: 'Data-driven forecasting and predictive modeling to make informed business decisions. Analyze trends, predict outcomes, and optimize operations.',
      icon: 'TrendingUp',
      path: '/services/ai-ml-services/predictive-analytics'
    },
    {
      title: 'AI Chatbots & Virtual Assistants',
      description: 'Intelligent conversational AI solutions for customer support, sales, and engagement. Build chatbots that understand context and provide human-like interactions.',
      icon: 'MessageCircle',
      path: '/services/ai-ml-services/ai-chatbots'
    },
    {
      title: 'Deep Learning Solutions',
      description: 'Advanced deep learning models using neural networks for complex pattern recognition, image processing, and sequence modeling. Solve complex AI challenges.',
      icon: 'Network',
      path: '/services/ai-ml-services/deep-learning'
    },
    {
      title: 'Data Science & Analytics',
      description: 'Comprehensive data science services including data analysis, statistical modeling, and business intelligence. Turn data into actionable business insights.',
      icon: 'BarChart',
      path: '/services/ai-ml-services/data-science'
    },
    {
      title: 'AI Consulting & Strategy',
      description: 'Strategic AI consulting to identify opportunities, develop AI roadmaps, and implement AI solutions that drive business value and competitive advantage.',
      icon: 'Lightbulb',
      path: '/services/ai-ml-services/ai-consulting'
    },
    {
      title: 'Reinforcement Learning',
      description: 'Build intelligent reinforcement learning agents that learn optimal strategies through interaction. We develop RL solutions for autonomous systems, game AI, robotics, and complex optimization problems.',
      icon: 'Network',
      path: '/services/ai-ml-services/reinforcement-learning'
    }
  ];

  const whyChooseUs = [
    {
      title: 'Tailored Solutions',
      description: 'Flexible and easy-to-customize services designed to fit your unique business needs.',
      icon: 'Settings'
    },
    {
      title: 'Cutting-Edge Designs',
      description: 'Modern, user-focused designs that enhance engagement and brand identity.',
      icon: 'Star'
    },
    {
      title: 'Proven Expertise',
      description: 'A team of experienced developers and designers delivering high-quality results.',
      icon: 'Trophy'
    },
    {
      title: 'Reliable Support',
      description: 'Dedicated customer support ensuring smooth project delivery and ongoing success.',
      icon: 'Phone'
    }
  ];

  const techStack = {
    frontend: ['React', 'Vue', 'Angular', 'Next.js', 'Nuxt.js'],
    backend: ['Node.js', 'Python', 'PHP', 'Java', 'Go'],
    cloud: ['AWS', 'GCP', 'Azure', 'Terraform', 'Kubernetes', 'Docker']
  };

  const industries = [
    { name: 'Fintech', icon: 'CreditCard' },
    { name: 'Healthcare', icon: 'Heart' },
    { name: 'Banking', icon: 'Building' },
    { name: 'Manufacturing', icon: 'Building2' },
    { name: 'Oil & Gas', icon: 'Zap' },
    { name: 'Real Estate', icon: 'Home' }
  ];

  const caseStudies = [
    {
      title: 'E-commerce Platform Development',
      client: 'Fashion Retail Client',
      industry: 'E-commerce',
      challenge: 'Legacy system causing 40% cart abandonment and poor user experience',
      solution: 'Built modern React-based platform with AI-powered recommendations and optimized checkout flow',
      results: ['300% increase in conversions', '60% reduction in load time', '95% customer satisfaction'],
      image: <Icon name="ShoppingCart" size={32} className="text-blue-600" />,
      technologies: ['React', 'Node.js', 'AWS', 'AI/ML'],
      duration: '12 weeks',
      team: '4 developers'
    },
    {
      title: 'Fintech Mobile App Development',
      client: 'Financial Services Client',
      industry: 'Financial Services',
      challenge: 'Outdated mobile app with poor user experience and security concerns',
      solution: 'Complete redesign with Flutter and advanced security features, biometric authentication',
      results: ['250% increase in user engagement', '99.9% uptime achieved', '50% faster transaction processing'],
      image: <Icon name="CreditCard" size={32} className="text-green-600" />,
      technologies: ['Flutter', 'Node.js', 'PostgreSQL', 'Blockchain'],
      duration: '16 weeks',
      team: '5 developers'
    },
    {
      title: 'Manufacturing IoT Dashboard',
      client: 'Manufacturing Client',
      industry: 'Manufacturing',
      challenge: 'Manual processes causing inefficiencies and lack of real-time monitoring',
      solution: 'IoT-enabled dashboard with real-time monitoring, predictive analytics, and automated alerts',
      results: ['35% increase in productivity', '80% reduction in downtime', 'Real-time analytics'],
      image: <Icon name="Factory" size={32} className="text-orange-600" />,
      technologies: ['IoT', 'React', 'Python', 'AWS IoT'],
      duration: '20 weeks',
      team: '6 developers'
    },
    {
      title: 'Real Estate CRM System',
      client: 'Real Estate Client',
      industry: 'Real Estate',
      challenge: 'Disconnected systems and manual processes causing lead management issues',
      solution: 'Integrated CRM with automated workflows, lead scoring, and property management features',
      results: ['200% increase in lead conversion', '70% time savings', 'Complete digital transformation'],
      image: <Icon name="Home" size={32} className="text-purple-600" />,
      technologies: ['Vue.js', 'Django', 'PostgreSQL', 'AWS'],
      duration: '14 weeks',
      team: '4 developers'
    }
  ];



  const clientLogos = [
    { name: 'Fashion Retail Client', logo: <Icon name="ShoppingCart" size={24} className="text-blue-600" />, industry: 'E-commerce' },
    { name: 'Financial Services Client', logo: <Icon name="CreditCard" size={24} className="text-green-600" />, industry: 'Fintech' },
    { name: 'Manufacturing Client', logo: <Icon name="Factory" size={24} className="text-orange-600" />, industry: 'Manufacturing' },
    { name: 'Real Estate Client', logo: <Icon name="Home" size={24} className="text-purple-600" />, industry: 'Real Estate' },
    { name: 'Healthcare Client', logo: <Icon name="Heart" size={24} className="text-red-600" />, industry: 'Healthcare' },
    { name: 'Education Client', logo: <Icon name="GraduationCap" size={24} className="text-indigo-600" />, industry: 'Education' }
  ];

  const frontendTechnologies = [
    {
      tech: 'React & Next.js',
      description: 'Modern React development with server-side rendering for optimal performance and SEO',
      benefits: ['Component reusability', 'SEO optimization', 'Fast loading'],
      projects: '15+'
    },
    {
      tech: 'Vue.js & Nuxt.js',
      description: 'Progressive framework perfect for building intuitive user interfaces',
      benefits: ['Easy learning curve', 'Flexible architecture', 'Great documentation'],
      projects: '8+'
    },
    {
      tech: 'Mobile Development',
      description: 'Cross-platform mobile apps using Flutter and React Native',
      benefits: ['Single codebase', 'Native performance', 'Faster development'],
      projects: '12+'
    }
  ];

  const backendTechnologies = [
    {
      tech: 'Node.js & Python',
      description: 'Scalable server-side applications with robust API development',
      benefits: ['High performance', 'Rich ecosystem', 'Real-time capabilities'],
      projects: '20+'
    },
    {
      tech: 'Cloud Platforms',
      description: 'AWS, Azure, and GCP deployment with automated scaling',
      benefits: ['Global reach', 'Auto-scaling', 'Cost optimization'],
      projects: '18+'
    },
    {
      tech: 'DevOps & CI/CD',
      description: 'Docker, Kubernetes, and automated deployment pipelines',
      benefits: ['Consistent deployments', 'Faster releases', 'Better reliability'],
      projects: '25+'
    }
  ];

  const aiMLTechnologies = [
    {
      tech: 'Machine Learning & Deep Learning',
      description: 'Custom ML models using TensorFlow, PyTorch, and Scikit-learn for predictive analytics and pattern recognition',
      benefits: ['Custom ML models', 'Neural networks', 'Model optimization'],
      projects: '15+'
    },
    {
      tech: 'Natural Language Processing',
      description: 'Advanced NLP solutions with Transformers, BERT, GPT, and Hugging Face for text analysis and chatbots',
      benefits: ['Sentiment analysis', 'Text generation', 'Language translation'],
      projects: '12+'
    },
    {
      tech: 'Computer Vision & AI Services',
      description: 'Image/video analysis with OpenCV, YOLO, and cloud AI services (OpenAI, Google Cloud AI, AWS AI)',
      benefits: ['Object detection', 'Image classification', 'OCR & recognition'],
      projects: '10+'
    }
  ];

  const industrySolutions = [
    {
      industry: 'Healthcare',
      icon: <Icon name="Heart" size={32} className="text-red-600" />,
      description: 'HIPAA-compliant solutions, patient management systems, telemedicine platforms',
      technologies: ['React', 'Node.js', 'PostgreSQL', 'AWS'],
      projects: '8+',
      features: ['Patient Portal', 'Appointment Booking', 'Medical Records', 'Telemedicine']
    },
    {
      industry: 'Fintech',
      icon: <Icon name="CreditCard" size={32} className="text-green-600" />,
      description: 'Secure payment processing, banking solutions, investment platforms',
      technologies: ['React', 'Python', 'Blockchain', 'Stripe API'],
      projects: '6+',
      features: ['Payment Gateway', 'KYC Verification', 'Risk Management', 'Mobile Banking']
    },
    {
      industry: 'E-commerce',
      icon: <Icon name="ShoppingCart" size={32} className="text-blue-600" />,
      description: 'Scalable online stores, inventory management, customer analytics',
      technologies: ['Next.js', 'Node.js', 'MongoDB', 'Shopify'],
      projects: '12+',
      features: ['Product Catalog', 'Shopping Cart', 'Order Management', 'Analytics Dashboard']
    },
    {
      industry: 'Education',
      icon: <Icon name="GraduationCap" size={32} className="text-indigo-600" />,
      description: 'Learning management systems, online courses, student portals',
      technologies: ['Vue.js', 'Django', 'MySQL', 'AWS'],
      projects: '5+',
      features: ['Course Management', 'Student Portal', 'Assessment Tools', 'Progress Tracking']
    },
    {
      industry: 'Real Estate',
      icon: <Icon name="Home" size={32} className="text-purple-600" />,
      description: 'Property management, CRM systems, virtual tours',
      technologies: ['React', 'Node.js', 'PostgreSQL', 'Google Maps'],
      projects: '7+',
      features: ['Property Listings', 'CRM Integration', 'Virtual Tours', 'Lead Management']
    },
    {
      industry: 'Manufacturing',
      icon: <Icon name="Factory" size={32} className="text-orange-600" />,
      description: 'IoT solutions, production monitoring, supply chain management',
      technologies: ['React', 'Python', 'IoT', 'Azure'],
      projects: '4+',
      features: ['Production Monitoring', 'Inventory Tracking', 'Quality Control', 'Predictive Analytics']
    }
  ];

  const aiMLIndustrySolutions = [
    {
      industry: 'Healthcare AI',
      icon: <Icon name="Brain" size={32} className="text-red-600" />,
      description: 'Deep learning models for radiology image analysis, AI-powered drug discovery pipelines, clinical decision support systems, and predictive patient outcome models',
      technologies: ['Deep Learning', 'Computer Vision', 'TensorFlow', 'Medical AI'],
      projects: '10+',
      features: ['Radiology AI', 'Drug Discovery ML', 'Clinical Decision AI', 'Predictive Diagnostics']
    },
    {
      industry: 'Financial AI',
      icon: <Icon name="TrendingUp" size={32} className="text-green-600" />,
      description: 'Real-time fraud detection systems, algorithmic trading bots, ML-based credit risk models, and automated compliance monitoring',
      technologies: ['Anomaly Detection', 'Reinforcement Learning', 'Time Series ML', 'Fraud AI'],
      projects: '12+',
      features: ['Real-time Fraud AI', 'Algorithmic Trading', 'ML Credit Models', 'Compliance Automation']
    },
    {
      industry: 'Retail Intelligence',
      icon: <Icon name="BarChart" size={32} className="text-blue-600" />,
      description: 'AI recommendation engines, demand forecasting models, dynamic pricing algorithms, and customer behavior prediction systems',
      technologies: ['Recommendation AI', 'Forecasting Models', 'Price Optimization', 'Behavioral AI'],
      projects: '15+',
      features: ['AI Recommendations', 'Demand Forecasting', 'Dynamic Pricing AI', 'Behavior Prediction']
    },
    {
      industry: 'Smart Manufacturing',
      icon: <Icon name="Cpu" size={32} className="text-orange-600" />,
      description: 'Computer vision for quality inspection, predictive maintenance ML models, AI-powered supply chain optimization, and automated defect classification',
      technologies: ['Computer Vision', 'Predictive ML', 'IoT AI', 'Defect Detection AI'],
      projects: '8+',
      features: ['Vision Quality Control', 'Predictive Maintenance', 'AI Supply Chain', 'Auto Defect Detection']
    },
    {
      industry: 'Autonomous Systems',
      icon: <Icon name="Network" size={32} className="text-purple-600" />,
      description: 'Reinforcement learning for autonomous vehicles, robotics AI, drone navigation systems, and intelligent automation solutions',
      technologies: ['Reinforcement Learning', 'Robotics AI', 'Computer Vision', 'Autonomous ML'],
      projects: '6+',
      features: ['Autonomous Vehicles', 'Robotics AI', 'Drone Navigation', 'Smart Automation']
    },
    {
      industry: 'Natural Language AI',
      icon: <Icon name="FileText" size={32} className="text-indigo-600" />,
      description: 'Large language models for content generation, sentiment analysis systems, intelligent chatbots, and document understanding AI',
      technologies: ['LLMs', 'NLP', 'Transformers', 'Sentiment AI'],
      projects: '14+',
      features: ['Content Generation AI', 'Sentiment Analysis', 'Intelligent Chatbots', 'Document AI']
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Discovery & Planning',
      description: 'We analyze your business requirements, target audience, and technical needs to create a comprehensive project roadmap and timeline.',
      icon: 'Search'
    },
    {
      step: '02',
      title: 'Design & Prototyping',
      description: 'Our design team creates wireframes, mockups, and interactive prototypes to visualize your project before development begins.',
      icon: 'Palette'
    },
    {
      step: '03',
      title: 'Development & Testing',
      description: 'Our developers build your solution using agile methodology with continuous testing, code reviews, and regular progress updates.',
      icon: 'Code'
    },
    {
      step: '04',
      title: 'Deployment & Support',
      description: 'We deploy your solution and provide ongoing support, maintenance, updates, and feature enhancements to ensure optimal performance.',
      icon: 'Rocket'
    }
  ];

  const comparisonFeatures = [
    {
      feature: 'Development Quality',
      octalchip: 'Expert developers, code reviews',
      outsourcing: 'Mixed expertise levels',
      freelancing: 'Unverified skills'
    },
    {
      feature: 'Project Delivery',
      octalchip: '2-4 months average',
      outsourcing: '2-6 months',
      freelancing: 'Unpredictable timelines'
    },
    {
      feature: 'Communication',
      octalchip: 'Direct access, regular updates',
      outsourcing: 'Limited communication',
      freelancing: 'Language barriers'
    },
    {
      feature: 'Support & Maintenance',
      octalchip: 'Guaranteed support',
      outsourcing: 'No guaranteed support',
      freelancing: 'Limited post-delivery help'
    },
    {
      feature: 'Technology Stack',
      octalchip: 'Modern, latest technologies',
      outsourcing: 'Often outdated',
      freelancing: 'Varies by freelancer'
    },
    {
      feature: 'AI/ML Expertise',
      octalchip: 'TensorFlow, PyTorch, NLP, Computer Vision',
      outsourcing: 'Limited AI capabilities',
      freelancing: 'Rare AI specialization'
    },
    {
      feature: 'AI Integration',
      octalchip: 'Seamless AI/ML integration in projects',
      outsourcing: 'Basic AI features only',
      freelancing: 'No AI integration support'
    },
    {
      feature: 'Data Science & Analytics',
      octalchip: 'Full data science pipeline & MLOps',
      outsourcing: 'Basic analytics only',
      freelancing: 'Limited data expertise'
    },
    {
      feature: 'Project Management',
      octalchip: 'Dedicated project manager',
      outsourcing: 'Minimal oversight',
      freelancing: 'Self-managed'
    }
  ];

  const securityFeatures = [
    {
      title: 'HTTPS/TLS Encryption',
      description: 'All data transmission secured with industry-standard encryption protocols',
      icon: <Icon name="Lock" size={24} className="text-gray-600" />,
      details: '256-bit SSL encryption'
    },
    {
      title: 'Data Privacy',
      description: 'Your data is never sold, rented, or shared with third parties',
      icon: <Icon name="Shield" size={24} className="text-green-600" />,
      details: 'GDPR compliant'
    },
    {
      title: 'Secure Infrastructure',
      description: 'Data stored in secure cloud environments with regular backups',
      icon: <Icon name="Cloud" size={24} className="text-blue-600" />,
      details: 'AWS/Azure certified'
    },
    {
      title: 'Access Control',
      description: 'Role-based access controls and multi-factor authentication',
      icon: <Icon name="Users" size={24} className="text-blue-600" />,
      details: 'Granular permissions'
    }
  ];

  const projectTypes = [
    'Web Application',
    'Mobile App',
    'E-commerce Platform',
    'Custom Software',
    'AI Integration'
  ];

  const complexityLevels = [
    'Simple',
    'Medium',
    'Complex',
    'Enterprise'
  ];

  const timelineOptions = [
    '2-4 weeks',
    '1-2 months',
    '2-3 months',
    '3-6 months',
    '6+ months'
  ];

  const additionalFeaturesOptions = [
    'User Authentication',
    'Payment Integration',
    'Admin Dashboard',
    'Mobile Responsive',
    'SEO Optimization',
    'API Development'
  ];

  // Calculator Logic
  const calculateProjectCost = () => {
    let baseCost = 0;
    let complexityMultiplier = 1;
    let timelineMultiplier = 1;
    let additionalCost = 0;

    // Base cost by project type
    const projectTypeCosts = {
      'Web Application': 5000,
      'Mobile App': 8000,
      'E-commerce Platform': 12000,
      'Custom Software': 15000,
      'AI Integration': 20000
    };

    // Complexity multipliers
    const complexityMultipliers = {
      'Simple': 0.7,
      'Medium': 1.0,
      'Complex': 1.5,
      'Enterprise': 2.0
    };

    // Timeline multipliers (rush jobs cost more)
    const timelineMultipliers = {
      '2-4 weeks': 1.5,
      '1-2 months': 1.2,
      '2-3 months': 1.0,
      '3-6 months': 0.9,
      '6+ months': 0.8
    };

    // Additional features cost
    const featureCosts = {
      'User Authentication': 1000,
      'Payment Integration': 2000,
      'Admin Dashboard': 1500,
      'Mobile Responsive': 1000,
      'SEO Optimization': 800,
      'API Development': 1200
    };

    baseCost = projectTypeCosts[calculatorState.projectType] || 5000;
    complexityMultiplier = complexityMultipliers[calculatorState.complexity] || 1.0;
    timelineMultiplier = timelineMultipliers[calculatorState.timeline] || 1.0;

    // Calculate additional features cost
    additionalCost = calculatorState.additionalFeatures.reduce((total, feature) => {
      return total + (featureCosts[feature] || 0);
    }, 0);

    const totalCost = Math.round((baseCost * complexityMultiplier * timelineMultiplier) + additionalCost);
    const minCost = Math.round(totalCost * 0.8);
    const maxCost = Math.round(totalCost * 1.2);

    return {
      min: minCost,
      max: maxCost,
      base: Math.round(baseCost * complexityMultiplier * timelineMultiplier),
      additional: additionalCost,
      testing: Math.round(totalCost * 0.1),
      deployment: Math.round(totalCost * 0.05)
    };
  };

  const costBreakdown = calculateProjectCost();

  // Calculator handlers
  const handleProjectTypeChange = (e) => {
    setCalculatorState(prev => ({ ...prev, projectType: e.target.value }));
  };

  const handleComplexityChange = (e) => {
    setCalculatorState(prev => ({ ...prev, complexity: e.target.value }));
  };

  const handleTimelineChange = (e) => {
    setCalculatorState(prev => ({ ...prev, timeline: e.target.value }));
  };

  const handleFeatureToggle = (feature) => {
    setCalculatorState(prev => ({
      ...prev,
      additionalFeatures: prev.additionalFeatures.includes(feature)
        ? prev.additionalFeatures.filter(f => f !== feature)
        : [...prev.additionalFeatures, feature]
    }));
  };

  const expertiseCards = [
    // Regular Services
    {
      title: 'Web Development',
      icon: <Icon name="Globe" size={24} className="text-blue-600" />,
      description: 'Custom web applications built with React, Vue.js, and modern frameworks for optimal performance and user experience.',
      technologies: ['React', 'Vue.js', 'Next.js', 'TypeScript'],
      projects: '25+',
      clients: 'E-commerce, SaaS, Enterprise',
      category: 'regular',
      path: '/services/web-development'
    },
    {
      title: 'Mobile App Development',
      icon: <Icon name="Smartphone" size={24} className="text-green-600" />,
      description: 'Cross-platform mobile apps using Flutter and React Native, delivering native performance across iOS and Android.',
      technologies: ['Flutter', 'React Native', 'iOS', 'Android'],
      projects: '15+',
      clients: 'Startups, Enterprises, Healthcare',
      category: 'regular',
      path: '/services/mobile-app-development'
    },
    {
      title: 'AI Integration',
      icon: <Icon name="Cpu" size={24} className="text-green-600" />,
      description: 'AI-powered solutions including machine learning models, chatbots, and intelligent automation systems.',
      technologies: ['Python', 'TensorFlow', 'OpenAI', 'ML'],
      projects: '10+',
      clients: 'Healthcare, Finance, E-commerce',
      category: 'regular',
      path: '/services/ai-integration'
    },
    {
      title: 'Cloud & DevOps',
      icon: <Icon name="Cloud" size={24} className="text-blue-600" />,
      description: 'Scalable cloud infrastructure on AWS, Azure, and GCP with automated CI/CD pipelines and monitoring.',
      technologies: ['AWS', 'Docker', 'Kubernetes', 'Terraform'],
      projects: '20+',
      clients: 'SaaS, Fintech, Manufacturing',
      category: 'regular',
      path: '/services/cloud-devops'
    },
    {
      title: 'UI/UX Design',
      icon: <Icon name="Palette" size={24} className="text-purple-600" />,
      description: 'User-centered design that combines aesthetics with functionality to create engaging digital experiences.',
      technologies: ['Figma', 'Adobe XD', 'Prototyping', 'Research'],
      projects: '30+',
      clients: 'All Industries',
      category: 'regular',
      path: '/services/ui-ux-design'
    },
    {
      title: 'Backend Development',
      icon: <Icon name="Server" size={24} className="text-green-600" />,
      description: 'Robust backend systems with microservices architecture, APIs, and database optimization for scalability.',
      technologies: ['Node.js', 'Python', 'PostgreSQL', 'MongoDB'],
      projects: '35+',
      clients: 'Enterprise, SaaS, Fintech',
      category: 'regular',
      path: '/services/backend-development'
    },
    // AI/ML Services
    {
      title: 'Machine Learning Development',
      icon: <Icon name="Brain" size={24} className="text-purple-600" />,
      description: 'Custom ML models for prediction, classification, and pattern recognition. Scalable machine learning solutions tailored to your business needs.',
      technologies: ['Python', 'TensorFlow', 'PyTorch', 'Scikit-learn'],
      projects: '15+',
      clients: 'Healthcare, Finance, E-commerce',
      category: 'ai-ml',
      path: '/services/ai-ml-services/machine-learning'
    },
    {
      title: 'Deep Learning Solutions',
      icon: <Icon name="Network" size={24} className="text-blue-600" />,
      description: 'Advanced deep learning models using neural networks for complex pattern recognition, image processing, and sequence modeling.',
      technologies: ['TensorFlow', 'PyTorch', 'Keras', 'Neural Networks'],
      projects: '12+',
      clients: 'Healthcare, Manufacturing, Fintech',
      category: 'ai-ml',
      path: '/services/ai-ml-services/deep-learning'
    },
    {
      title: 'Natural Language Processing',
      icon: <Icon name="FileText" size={24} className="text-green-600" />,
      description: 'Advanced NLP solutions for text analysis, sentiment analysis, language translation, and intelligent text processing.',
      technologies: ['NLTK', 'spaCy', 'Transformers', 'BERT'],
      projects: '10+',
      clients: 'E-commerce, Customer Service, Content',
      category: 'ai-ml',
      path: '/services/ai-ml-services/natural-language-processing'
    },
    {
      title: 'Computer Vision',
      icon: <Icon name="Eye" size={24} className="text-orange-600" />,
      description: 'Image and video analysis solutions for object detection, recognition, classification, and content moderation.',
      technologies: ['OpenCV', 'YOLO', 'CNN', 'Image Processing'],
      projects: '8+',
      clients: 'Manufacturing, Healthcare, Security',
      category: 'ai-ml',
      path: '/services/ai-ml-services/computer-vision'
    },
    {
      title: 'Predictive Analytics',
      icon: <Icon name="TrendingUp" size={24} className="text-blue-600" />,
      description: 'Data-driven forecasting and predictive modeling to make informed business decisions. Analyze trends, predict outcomes, and optimize operations.',
      technologies: ['Python', 'Pandas', 'Scikit-learn', 'Time Series'],
      projects: '12+',
      clients: 'Finance, Retail, Healthcare',
      category: 'ai-ml',
      path: '/services/ai-ml-services/predictive-analytics'
    },
    {
      title: 'AI Chatbots & Virtual Assistants',
      icon: <Icon name="MessageCircle" size={24} className="text-green-600" />,
      description: 'Intelligent conversational AI solutions for customer support, sales, and engagement. Build chatbots that understand context and provide human-like interactions.',
      technologies: ['OpenAI', 'Dialogflow', 'Rasa', 'NLP'],
      projects: '18+',
      clients: 'E-commerce, Customer Service, Healthcare',
      category: 'ai-ml',
      path: '/services/ai-ml-services/ai-chatbots'
    },
    {
      title: 'Data Science & Analytics',
      icon: <Icon name="BarChart" size={24} className="text-orange-600" />,
      description: 'Comprehensive data science services including data analysis, statistical modeling, and business intelligence. Turn data into actionable business insights.',
      technologies: ['Python', 'R', 'Tableau', 'Power BI'],
      projects: '22+',
      clients: 'All Industries',
      category: 'ai-ml',
      path: '/services/ai-ml-services/data-science'
    },
    {
      title: 'AI Consulting & Strategy',
      icon: <Icon name="Lightbulb" size={24} className="text-yellow-600" />,
      description: 'Strategic AI consulting to identify opportunities, develop AI roadmaps, and implement AI solutions that drive business value.',
      technologies: ['AI Strategy', 'MLOps', 'Data Science', 'Analytics'],
      projects: '20+',
      clients: 'All Industries',
      category: 'ai-ml',
      path: '/services/ai-ml-services/ai-consulting'
    },
    {
      title: 'Reinforcement Learning',
      icon: <Icon name="Network" size={24} className="text-purple-600" />,
      description: 'Build intelligent reinforcement learning agents that learn optimal strategies through interaction. Solutions for autonomous systems, game AI, and robotics.',
      technologies: ['OpenAI Gym', 'TensorFlow', 'PyTorch', 'RL Algorithms'],
      projects: '6+',
      clients: 'Gaming, Robotics, Autonomous Systems',
      category: 'ai-ml',
      path: '/services/ai-ml-services/reinforcement-learning'
    }
  ];

  const technologyPartners = [
    { name: 'AWS', logo: <Icon name="Cloud" size={32} className="text-orange-500" />, category: 'Cloud' },
    { name: 'Azure', logo: <Icon name="Cloud" size={32} className="text-blue-500" />, category: 'Cloud' },
    { name: 'Google Cloud', logo: <Icon name="Cloud" size={32} className="text-blue-600" />, category: 'Cloud' },
    { name: 'Docker', logo: <Icon name="Container" size={32} className="text-blue-400" />, category: 'DevOps' },
    { name: 'Kubernetes', logo: <Icon name="Settings" size={32} className="text-blue-600" />, category: 'DevOps' },
    { name: 'GitHub', logo: <Icon name="Github" size={32} className="text-gray-800" />, category: 'Development' },
    { name: 'GitLab', logo: <Icon name="GitBranch" size={32} className="text-orange-500" />, category: 'Development' },
    { name: 'Jenkins', logo: <Icon name="Settings" size={32} className="text-gray-600" />, category: 'CI/CD' },
    { name: 'Stripe', logo: <Icon name="CreditCard" size={32} className="text-purple-600" />, category: 'Payments' },
    { name: 'PayPal', logo: <Icon name="CreditCard" size={32} className="text-blue-600" />, category: 'Payments' },
    { name: 'MongoDB', logo: <Icon name="Database" size={32} className="text-green-600" />, category: 'Database' },
    { name: 'PostgreSQL', logo: <Icon name="Database" size={32} className="text-blue-700" />, category: 'Database' }
  ];

  const developmentTimeline = [
    {
      phase: 'Discovery & Analysis',
      duration: '1-2 weeks',
      description: 'Understanding your business requirements, target audience, and technical constraints',
      deliverables: ['Requirements Document', 'Technical Architecture', 'Project Timeline'],
      icon: <Icon name="Search" size={24} className="text-blue-600" />
    },
    {
      phase: 'Design & Prototyping',
      duration: '2-3 weeks',
      description: 'Creating wireframes, mockups, and interactive prototypes for user validation',
      deliverables: ['Wireframes', 'UI/UX Design', 'Interactive Prototype'],
      icon: <Icon name="Palette" size={24} className="text-purple-600" />
    },
    {
      phase: 'Development & Testing',
      duration: '4-8 weeks',
      description: 'Building your solution with continuous testing and regular progress updates',
      deliverables: ['MVP Development', 'Quality Assurance', 'Code Reviews'],
      icon: <Icon name="Settings" size={24} className="text-green-600" />
    },
    {
      phase: 'Deployment & Launch',
      duration: '1 week',
      description: 'Deploying to production, monitoring performance, and providing launch support',
      deliverables: ['Production Deployment', 'Performance Monitoring', 'Launch Support'],
      icon: <Icon name="Rocket" size={24} className="text-orange-600" />
    },
    {
      phase: 'Support & Maintenance',
      duration: 'Ongoing',
      description: 'Providing ongoing support, updates, and feature enhancements',
      deliverables: ['Bug Fixes', 'Feature Updates', 'Performance Optimization'],
      icon: <Icon name="Wrench" size={24} className="text-gray-600" />
    }
  ];

  const aiMLDevelopmentTimeline = [
    {
      phase: 'Data Discovery & Analysis',
      duration: '2-3 weeks',
      description: 'Understanding your business problem, data sources, and AI/ML requirements. We analyze data quality, availability, and define success metrics.',
      deliverables: ['Data Assessment Report', 'AI/ML Strategy Document', 'Success Metrics Definition'],
      icon: <Icon name="Search" size={24} className="text-blue-600" />
    },
    {
      phase: 'Data Preparation & Feature Engineering',
      duration: '3-4 weeks',
      description: 'Collecting, cleaning, and preparing data for machine learning. We perform feature engineering, data validation, and create training datasets.',
      deliverables: ['Cleaned Dataset', 'Feature Engineering Pipeline', 'Data Validation Reports'],
      icon: <Icon name="Database" size={24} className="text-purple-600" />
    },
    {
      phase: 'Model Development & Training',
      duration: '4-8 weeks',
      description: 'Building and training machine learning models using state-of-the-art algorithms. We experiment with different approaches and optimize for performance.',
      deliverables: ['Trained ML Models', 'Model Performance Reports', 'Algorithm Comparison'],
      icon: <Icon name="Brain" size={24} className="text-green-600" />
    },
    {
      phase: 'Model Validation & Testing',
      duration: '2-3 weeks',
      description: 'Rigorous testing and validation of models using cross-validation, A/B testing, and real-world scenarios to ensure accuracy and reliability.',
      deliverables: ['Validation Results', 'Performance Metrics', 'Bias & Fairness Analysis'],
      icon: <Icon name="CheckCircle" size={24} className="text-orange-600" />
    },
    {
      phase: 'Deployment & Integration',
      duration: '2-3 weeks',
      description: 'Deploying models to production with MLOps pipelines, API integration, and monitoring systems for continuous performance tracking.',
      deliverables: ['Production Deployment', 'MLOps Pipeline', 'Monitoring Dashboard'],
      icon: <Icon name="Rocket" size={24} className="text-orange-600" />
    },
    {
      phase: 'Optimization & Maintenance',
      duration: 'Ongoing',
      description: 'Continuous model monitoring, retraining with new data, and performance optimization to maintain accuracy and adapt to changing conditions.',
      deliverables: ['Model Updates', 'Performance Reports', 'Retraining Pipeline'],
      icon: <Icon name="TrendingUp" size={24} className="text-gray-600" />
    }
  ];

  const successMetrics = [
    { metric: '25+', label: 'Projects Delivered', description: 'Successfully completed development projects' },
    { metric: '99.9%', label: 'Client Satisfaction', description: 'Based on post-project feedback surveys' },
    { metric: '48hrs', label: 'Average Response Time', description: 'Quick response to client queries and requests' },
    { metric: '100%', label: 'On-Time Delivery', description: 'Projects delivered within agreed timelines' }
  ];

  const globalRegions = [
    { region: 'North America', clients: '8+', timezone: 'EST/PST', focus: 'Enterprise Solutions' },
    { region: 'Europe', clients: '6+', timezone: 'GMT/CET', focus: 'Fintech & Healthcare' },
    { region: 'Asia Pacific', clients: '4+', timezone: 'IST/JST', focus: 'E-commerce & Mobile' },
    { region: 'Middle East', clients: '3+', timezone: 'GST', focus: 'Real Estate & Manufacturing' }
  ];

  const globalBenefits = [
    {
      title: '24/7 Development Cycle',
      description: 'Round-the-clock development with teams across different time zones',
      icon: <Icon name="Globe" size={24} className="text-blue-600" />
    },
    {
      title: 'Cultural Understanding',
      description: 'Deep understanding of different markets and user behaviors',
      icon: <Icon name="Handshake" size={24} className="text-blue-600" />
    },
    {
      title: 'Compliance & Security',
      description: 'Adherence to international standards and data protection laws',
      icon: <Icon name="Shield" size={24} className="text-green-600" />
    },
    {
      title: 'Scalable Solutions',
      description: 'Architecture designed to handle global traffic and users',
      icon: <Icon name="Chart" size={24} className="text-orange-600" />
    }
  ];

  return (
    <div className="min-h-screen bg-[#f4faf8] overflow-x-hidden">
      <Header />
      <SEO
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        ogImage={seoData.ogImage}
        structuredData={seoData.structuredData}
        geoData={seoData.geoData}
        localBusinessData={seoData.localBusinessData}
        serviceArea={seoData.serviceArea}
        faqData={seoData.faqData}
        voiceSearchKeywords={seoData.voiceSearchKeywords}
        conversationalQueries={seoData.conversationalQueries}
        featuredSnippetContent={seoData.featuredSnippetContent}
        listData={seoData.listData}
        tableData={seoData.tableData}
      />

      {/* Our Development Excellence Section */}
      <section id="hero" className="py-6 sm:py-8 md:py-12 lg:py-16 xl:py-20 bg-gradient-to-br from-[#00d690]/5 to-[#00d690]/10 relative overflow-hidden">
        {/* Animated Background Effects - Hidden on mobile for performance */}
        <div className="absolute inset-0 hidden sm:block">
          {/* Floating animated dots - Responsive positioning */}
          <div className="absolute top-12 sm:top-16 md:top-20 left-8 sm:left-12 md:left-20 w-1.5 sm:w-2 h-1.5 sm:h-2 bg-[#00d690] rounded-full animate-pulse"></div>
          <div className="absolute top-24 sm:top-32 md:top-40 right-16 sm:right-24 md:right-32 w-1 sm:w-1.5 h-1 sm:h-1.5 bg-[#00d690]/60 rounded-full animate-pulse delay-1000"></div>
          <div className="absolute bottom-20 sm:bottom-24 md:bottom-32 left-16 sm:left-24 md:left-40 w-1 sm:w-1.5 h-1 sm:h-1.5 bg-[#00d690]/80 rounded-full animate-pulse delay-500"></div>
          <div className="absolute bottom-12 sm:bottom-16 md:bottom-20 right-8 sm:right-12 md:right-20 w-1.5 sm:w-2 h-1.5 sm:h-2 bg-[#00d690]/70 rounded-full animate-pulse delay-700"></div>
          <div className="absolute top-32 sm:top-40 md:top-60 left-1/2 w-1 sm:w-1.5 h-1 sm:h-1.5 bg-[#00d690]/50 rounded-full animate-pulse delay-300"></div>

          {/* Additional floating elements */}
          <div className="absolute top-20 sm:top-24 md:top-32 right-8 sm:right-12 md:right-20 w-1 sm:w-1.5 h-1 sm:h-1.5 bg-[#00b876]/60 rounded-full animate-bounce delay-200"></div>
          <div className="absolute bottom-24 sm:bottom-32 md:bottom-40 right-16 sm:right-24 md:right-40 w-1 sm:w-1.5 h-1 sm:h-1.5 bg-[#00d690]/40 rounded-full animate-pulse delay-800"></div>
          <div className="absolute top-48 sm:top-56 md:top-80 left-16 sm:left-20 md:left-32 w-1.5 sm:w-2 h-1.5 sm:h-2 bg-[#00b876]/50 rounded-full animate-pulse delay-400"></div>

          {/* Connecting lines animation */}
          <div className="absolute top-1/4 left-1/4 w-px h-12 sm:h-16 md:h-20 bg-gradient-to-b from-transparent via-[#00d690]/30 to-transparent animate-pulse delay-600"></div>
          <div className="absolute top-3/4 right-1/4 w-px h-10 sm:h-12 md:h-16 bg-gradient-to-b from-transparent via-[#00b876]/30 to-transparent animate-pulse delay-900"></div>
        </div>

        <div className="container mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 2xl:px-16 relative z-10">
          {/* Mobile Layout (320px - 639px) */}
          <div className="block sm:hidden">
            {/* Mobile Header */}
            <div className="text-center mb-5 sm:mb-6">
              <div className="inline-block bg-gray-800/80 border border-[#00d690]/30 rounded-lg px-3 sm:px-4 py-1.5 sm:py-2 mb-3 sm:mb-4 backdrop-blur-sm">
                <span className="text-white text-xs sm:text-sm font-medium leading-tight">Transform your ideas into powerful digital solutions</span>
              </div>

              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4 leading-tight px-2">
                Build <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00d690] to-[#00b876]">Exceptional Software</span><br />
                with Expert Development Team
              </h2>

              <p className="text-sm sm:text-base text-gray-600 max-w-xs mx-auto mb-5 sm:mb-6 leading-relaxed px-3">
                OctalChip delivers cutting-edge software solutions with our skilled team of developers, designers, and engineers.
              </p>

              <div className="flex flex-col gap-3 sm:gap-3 justify-center px-3 sm:px-4">
                <button
                  onClick={handleContactForm}
                  className="bg-gradient-to-r from-[#00d690] to-[#00b876] text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg text-sm sm:text-base font-semibold hover:from-[#00b876] hover:to-[#00a066] transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  Start Your Project
                </button>
                <button
                  onClick={() => navigateToPage('/services')}
                  className="bg-transparent border-2 border-[#00d690] text-[#00d690] px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg text-sm sm:text-base font-semibold hover:bg-[#00d690] hover:text-white transition-all duration-300"
                >
                  Explore Services
                </button>
              </div>
            </div>

            {/* Mobile Service Cards */}
            <div className="grid grid-cols-1 gap-2.5 sm:gap-3">
              <div className="bg-gray-800/90 border border-[#00d690]/60 rounded-xl p-3 sm:p-4 backdrop-blur-sm overflow-hidden">
                <div className="flex items-start mb-2.5 sm:mb-3">
                  <div className="w-9 sm:w-10 h-9 sm:h-10 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-lg flex items-center justify-center mr-2.5 sm:mr-3 shadow-lg flex-shrink-0">
                    <Icon name="Code" size={18} className="sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-white font-semibold text-sm sm:text-base leading-snug mb-0.5 whitespace-normal">Full-Stack Development</h3>
                    <p className="text-gray-400 text-xs sm:text-sm leading-tight whitespace-normal">Web Applications</p>
                  </div>
                </div>
                <div className="text-gray-300 text-xs sm:text-sm leading-relaxed mt-2" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>End-to-end applications with modern frameworks and technologies</div>
              </div>

              <div className="bg-gray-800/90 border border-[#00d690]/60 rounded-xl p-3 sm:p-4 backdrop-blur-sm overflow-hidden">
                <div className="flex items-start mb-2.5 sm:mb-3">
                  <div className="w-9 sm:w-10 h-9 sm:h-10 bg-gradient-to-br from-[#00d690]/80 to-[#00b876] rounded-lg flex items-center justify-center mr-2.5 sm:mr-3 shadow-lg flex-shrink-0">
                    <Icon name="Smartphone" size={18} className="sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-white font-semibold text-sm sm:text-base leading-snug mb-0.5 whitespace-normal">Mobile Development</h3>
                    <p className="text-gray-400 text-xs sm:text-sm leading-tight whitespace-normal">iOS & Android</p>
                  </div>
                </div>
                <div className="text-gray-300 text-xs sm:text-sm leading-relaxed mt-2" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>Native and cross-platform mobile solutions for all devices</div>
              </div>

              <div className="bg-gray-800/90 border border-[#00d690]/60 rounded-xl p-3 sm:p-4 backdrop-blur-sm overflow-hidden">
                <div className="flex items-start mb-2.5 sm:mb-3">
                  <div className="w-9 sm:w-10 h-9 sm:h-10 bg-gradient-to-br from-[#00b876] to-[#00a066] rounded-lg flex items-center justify-center mr-2.5 sm:mr-3 shadow-lg flex-shrink-0">
                    <Icon name="Cloud" size={18} className="sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-white font-semibold text-sm sm:text-base leading-snug mb-0.5 whitespace-normal">Cloud & DevOps</h3>
                    <p className="text-gray-400 text-xs sm:text-sm leading-tight whitespace-normal">Infrastructure</p>
                  </div>
                </div>
                <div className="text-gray-300 text-xs sm:text-sm leading-relaxed mt-2" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>Scalable cloud infrastructure and automated deployment</div>
              </div>

              <div className="bg-gray-800/90 border border-[#00d690]/60 rounded-xl p-3 sm:p-4 backdrop-blur-sm overflow-hidden">
                <div className="flex items-start mb-2.5 sm:mb-3">
                  <div className="w-9 sm:w-10 h-9 sm:h-10 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-lg flex items-center justify-center mr-2.5 sm:mr-3 shadow-lg flex-shrink-0">
                    <Icon name="Zap" size={18} className="sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-white font-semibold text-sm sm:text-base leading-snug mb-0.5 whitespace-normal">AI & ML Solutions</h3>
                    <p className="text-gray-400 text-xs sm:text-sm leading-tight whitespace-normal">Intelligent Systems</p>
                  </div>
                </div>
                <div className="text-gray-300 text-xs sm:text-sm leading-relaxed mt-2" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>AI-powered applications and machine learning solutions</div>
              </div>
            </div>
          </div>

          {/* Small Tablet Layout (640px - 767px) */}
          <div className="hidden sm:block md:hidden">
            {/* Rounded rectangle main container for small tablets */}
            <div className="relative max-w-4xl mx-auto">
              {/* Outer glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#00d690]/20 to-[#00b876]/20 rounded-[20px] min-h-[550px] blur-xl"></div>

              {/* Main rounded rectangle background */}
              <div className="relative bg-gradient-to-br from-gray-800/80 to-gray-900/80 rounded-[20px] min-h-[550px] border border-[#00d690]/30 backdrop-blur-sm shadow-2xl shadow-[#00d690]/10">
                {/* Inner rounded glow effect */}
                <div className="absolute inset-4 sm:inset-5 bg-gradient-to-br from-[#00d690]/10 to-[#00b876]/10 rounded-[16px] sm:rounded-[18px] border border-[#00d690]/20"></div>

                {/* Content positioned within container */}
                <div className="relative flex flex-col items-center justify-center p-5 sm:p-6 min-h-[550px]">
                  {/* Tagline */}
                  <div className="inline-block bg-gray-800/80 border border-[#00d690]/30 rounded-lg px-3 sm:px-4 py-1.5 sm:py-2 mb-4 sm:mb-5 backdrop-blur-sm">
                    <span className="text-white text-xs sm:text-sm font-medium">Transform your ideas into powerful digital solutions</span>
                  </div>

                  {/* Main Headline */}
                  <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 sm:mb-5 leading-tight text-center px-3 sm:px-4">
                    Build <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00d690] to-[#00b876]">Exceptional Software</span><br />
                    with Expert Development Team
                  </h2>

                  {/* Description */}
                  <p className="text-sm sm:text-base text-gray-300 max-w-xl mx-auto mb-6 sm:mb-7 leading-relaxed text-center px-3 sm:px-4">
                    OctalChip delivers cutting-edge software solutions with our skilled team of developers, designers, and engineers who bring your vision to life.
                  </p>

                  {/* CTA Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-3 sm:px-4 mb-6 sm:mb-7 w-full max-w-md">
                    <button
                      onClick={handleContactForm}
                      className="bg-gradient-to-r from-[#00d690] to-[#00b876] text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg text-sm sm:text-base font-semibold hover:from-[#00b876] hover:to-[#00a066] transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-[#00d690]/25"
                    >
                      Start Your Project
                    </button>
                    <button
                      onClick={() => navigateToPage('/services')}
                      className="bg-transparent border-2 border-[#00d690] text-[#00d690] px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg text-sm sm:text-base font-semibold hover:bg-[#00d690] hover:text-white transition-all duration-300 backdrop-blur-sm"
                    >
                      Explore Services
                    </button>
                  </div>

                  {/* Service Cards Grid for Small Tablets */}
                  <div className="grid grid-cols-2 gap-3 sm:gap-4 w-full max-w-xl px-3 sm:px-4">
                    <div className="bg-gray-800/90 border border-[#00d690]/60 rounded-xl p-3.5 sm:p-4 backdrop-blur-sm hover:border-[#00d690] transition-all duration-300 hover:shadow-xl hover:shadow-[#00d690]/30 overflow-hidden">
                      <div className="flex items-start mb-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-lg flex items-center justify-center mr-3 shadow-lg flex-shrink-0">
                          <Icon name="Code" size={20} className="text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-white font-semibold text-sm sm:text-base leading-snug mb-1 whitespace-normal">Full-Stack Development</h3>
                          <p className="text-gray-400 text-xs sm:text-sm leading-tight whitespace-normal">Web Applications</p>
                        </div>
                      </div>
                      <div className="text-gray-300 text-xs sm:text-sm leading-relaxed mt-2" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>End-to-end applications with modern frameworks and technologies</div>
                    </div>

                    <div className="bg-gray-800/90 border border-[#00d690]/60 rounded-xl p-3.5 sm:p-4 backdrop-blur-sm hover:border-[#00d690] transition-all duration-300 hover:shadow-xl hover:shadow-[#00d690]/30 overflow-hidden">
                      <div className="flex items-start mb-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-[#00d690]/80 to-[#00b876] rounded-lg flex items-center justify-center mr-3 shadow-lg flex-shrink-0">
                          <Icon name="Smartphone" size={20} className="text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-white font-semibold text-sm sm:text-base leading-snug mb-1 whitespace-normal">Mobile Development</h3>
                          <p className="text-gray-400 text-xs sm:text-sm leading-tight whitespace-normal">iOS & Android</p>
                        </div>
                      </div>
                      <div className="text-gray-300 text-xs sm:text-sm leading-relaxed mt-2" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>Native and cross-platform mobile solutions for all devices</div>
                    </div>

                    <div className="bg-gray-800/90 border border-[#00d690]/60 rounded-xl p-3.5 sm:p-4 backdrop-blur-sm hover:border-[#00d690] transition-all duration-300 hover:shadow-xl hover:shadow-[#00d690]/30 overflow-hidden">
                      <div className="flex items-start mb-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-[#00b876] to-[#00a066] rounded-lg flex items-center justify-center mr-3 shadow-lg flex-shrink-0">
                          <Icon name="Cloud" size={20} className="text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-white font-semibold text-sm sm:text-base leading-snug mb-1 whitespace-normal">Cloud & DevOps</h3>
                          <p className="text-gray-400 text-xs sm:text-sm leading-tight whitespace-normal">Infrastructure</p>
                        </div>
                      </div>
                      <div className="text-gray-300 text-xs sm:text-sm leading-relaxed mt-2" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>Scalable cloud infrastructure and automated deployment</div>
                    </div>

                    <div className="bg-gray-800/90 border border-[#00d690]/60 rounded-xl p-3.5 sm:p-4 backdrop-blur-sm hover:border-[#00d690] transition-all duration-300 hover:shadow-xl hover:shadow-[#00d690]/30 overflow-hidden">
                      <div className="flex items-start mb-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-lg flex items-center justify-center mr-3 shadow-lg flex-shrink-0">
                          <Icon name="Zap" size={20} className="text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-white font-semibold text-sm sm:text-base leading-snug mb-1 whitespace-normal">AI & ML Solutions</h3>
                          <p className="text-gray-400 text-xs sm:text-sm leading-tight whitespace-normal">Intelligent Systems</p>
                        </div>
                      </div>
                      <div className="text-gray-300 text-xs sm:text-sm leading-relaxed mt-2" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>AI-powered applications and machine learning solutions</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tablet Layout (768px - 1023px) */}
          <div className="hidden md:block lg:hidden">
            {/* Rounded rectangle main container for tablets */}
            <div className="relative max-w-5xl mx-auto">
              {/* Outer glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#00d690]/20 to-[#00b876]/20 rounded-[25px] min-h-[600px] blur-xl"></div>

              {/* Main rounded rectangle background */}
              <div className="relative bg-gradient-to-br from-gray-800/80 to-gray-900/80 rounded-[25px] min-h-[600px] border border-[#00d690]/30 backdrop-blur-sm shadow-2xl shadow-[#00d690]/10">
                {/* Inner rounded glow effect */}
                <div className="absolute inset-5 md:inset-6 bg-gradient-to-br from-[#00d690]/10 to-[#00b876]/10 rounded-[20px] border border-[#00d690]/20"></div>

                {/* Content positioned within container */}
                <div className="relative flex flex-col items-center justify-center p-7 md:p-8 min-h-[600px]">
                  {/* Tagline */}
                  <div className="inline-block bg-gray-800/80 border border-[#00d690]/30 rounded-lg px-4 py-2 mb-5 md:mb-6 backdrop-blur-sm">
                    <span className="text-white text-sm font-medium">Transform your ideas into powerful digital solutions</span>
                  </div>

                  {/* Main Headline */}
                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-5 md:mb-6 leading-tight text-center px-4">
                    Build <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00d690] to-[#00b876]">Exceptional Software</span><br />
                    with Expert Development Team
                  </h2>

                  {/* Description */}
                  <p className="text-base md:text-lg text-gray-300 max-w-2xl mx-auto mb-7 md:mb-8 leading-relaxed text-center px-4">
                    OctalChip delivers cutting-edge software solutions with our skilled team of developers, designers, and engineers who bring your vision to life.
                  </p>

                  {/* CTA Buttons */}
                  <div className="flex flex-row gap-3 md:gap-4 justify-center px-4 mb-7 md:mb-8">
                    <button
                      onClick={handleContactForm}
                      className="bg-gradient-to-r from-[#00d690] to-[#00b876] text-white px-6 py-3 rounded-lg text-base font-semibold hover:from-[#00b876] hover:to-[#00a066] transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-[#00d690]/25"
                    >
                      Start Your Project
                    </button>
                    <button
                      onClick={() => navigateToPage('/services')}
                      className="bg-transparent border-2 border-[#00d690] text-[#00d690] px-6 py-3 rounded-lg text-base font-semibold hover:bg-[#00d690] hover:text-white transition-all duration-300 backdrop-blur-sm"
                    >
                      Explore Services
                    </button>
                  </div>

                  {/* Service Cards Grid for Tablets */}
                  <div className="grid grid-cols-2 gap-4 w-full max-w-2xl px-4">
                    <div className="bg-gray-800/90 border border-[#00d690]/60 rounded-xl p-4 backdrop-blur-sm hover:border-[#00d690] transition-all duration-300 hover:shadow-xl hover:shadow-[#00d690]/30 overflow-hidden">
                      <div className="flex items-start mb-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-lg flex items-center justify-center mr-3 shadow-lg flex-shrink-0">
                          <Icon name="Code" size={20} className="text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-white font-semibold text-sm leading-snug mb-1 whitespace-normal">Full-Stack Development</h3>
                          <p className="text-gray-400 text-xs leading-tight whitespace-normal">Web Applications</p>
                        </div>
                      </div>
                      <div className="text-gray-300 text-xs leading-relaxed mt-2" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>End-to-end applications with modern frameworks and technologies</div>
                    </div>

                    <div className="bg-gray-800/90 border border-[#00d690]/60 rounded-xl p-4 backdrop-blur-sm hover:border-[#00d690] transition-all duration-300 hover:shadow-xl hover:shadow-[#00d690]/30 overflow-hidden">
                      <div className="flex items-start mb-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-[#00d690]/80 to-[#00b876] rounded-lg flex items-center justify-center mr-3 shadow-lg flex-shrink-0">
                          <Icon name="Smartphone" size={20} className="text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-white font-semibold text-sm leading-snug mb-1 whitespace-normal">Mobile Development</h3>
                          <p className="text-gray-400 text-xs leading-tight whitespace-normal">iOS & Android</p>
                        </div>
                      </div>
                      <div className="text-gray-300 text-xs leading-relaxed mt-2" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>Native and cross-platform mobile solutions for all devices</div>
                    </div>

                    <div className="bg-gray-800/90 border border-[#00d690]/60 rounded-xl p-4 backdrop-blur-sm hover:border-[#00d690] transition-all duration-300 hover:shadow-xl hover:shadow-[#00d690]/30 overflow-hidden">
                      <div className="flex items-start mb-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-[#00b876] to-[#00a066] rounded-lg flex items-center justify-center mr-3 shadow-lg flex-shrink-0">
                          <Icon name="Cloud" size={20} className="text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-white font-semibold text-sm leading-snug mb-1 whitespace-normal">Cloud & DevOps</h3>
                          <p className="text-gray-400 text-xs leading-tight whitespace-normal">Infrastructure</p>
                        </div>
                      </div>
                      <div className="text-gray-300 text-xs leading-relaxed mt-2" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>Scalable cloud infrastructure and automated deployment</div>
                    </div>

                    <div className="bg-gray-800/90 border border-[#00d690]/60 rounded-xl p-4 backdrop-blur-sm hover:border-[#00d690] transition-all duration-300 hover:shadow-xl hover:shadow-[#00d690]/30 overflow-hidden">
                      <div className="flex items-start mb-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-lg flex items-center justify-center mr-3 shadow-lg flex-shrink-0">
                          <Icon name="Zap" size={20} className="text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-white font-semibold text-sm leading-snug mb-1 whitespace-normal">AI & ML Solutions</h3>
                          <p className="text-gray-400 text-xs leading-tight whitespace-normal">Intelligent Systems</p>
                        </div>
                      </div>
                      <div className="text-gray-300 text-xs leading-relaxed mt-2" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>AI-powered applications and machine learning solutions</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Desktop Layout (1024px+) */}
          <div className="hidden lg:block">
            {/* Rounded rectangle main container (Uplers style) */}
            <div className="relative max-w-6xl xl:max-w-7xl 2xl:max-w-7xl mx-auto">
              {/* Outer glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#00d690]/20 to-[#00b876]/20 rounded-[30px] lg:rounded-[35px] xl:rounded-[40px] 2xl:rounded-[45px] min-h-[500px] lg:min-h-[550px] xl:min-h-[600px] 2xl:min-h-[650px] max-h-[700px] blur-xl"></div>

              {/* Main rounded rectangle background (Uplers style) */}
              <div className="relative bg-gradient-to-br from-gray-800/80 to-gray-900/80 rounded-[30px] lg:rounded-[35px] xl:rounded-[40px] 2xl:rounded-[45px] min-h-[500px] lg:min-h-[550px] xl:min-h-[600px] 2xl:min-h-[650px] max-h-[700px] border border-[#00d690]/30 backdrop-blur-sm shadow-2xl shadow-[#00d690]/10">
                {/* Inner rounded glow effect */}
                <div className="absolute inset-6 lg:inset-7 xl:inset-8 2xl:inset-10 bg-gradient-to-br from-[#00d690]/10 to-[#00b876]/10 rounded-[24px] lg:rounded-[28px] xl:rounded-[32px] 2xl:rounded-[36px] border border-[#00d690]/20"></div>

                {/* Additional inner glow layers */}
                <div className="absolute inset-8 lg:inset-10 xl:inset-12 2xl:inset-14 bg-gradient-to-br from-[#00d690]/5 to-[#00b876]/5 rounded-[18px] lg:rounded-[22px] xl:rounded-[24px] 2xl:rounded-[28px]"></div>

                {/* Content positioned within oval */}
                <div className="absolute inset-0 flex flex-col items-center justify-center p-6 lg:p-8 xl:p-10 2xl:p-12">
                  {/* Tagline */}
                  <div className="inline-block bg-gray-800/80 border border-[#00d690]/30 rounded-lg px-3 lg:px-4 xl:px-5 py-1.5 lg:py-2 mb-4 lg:mb-5 xl:mb-6 backdrop-blur-sm">
                    <span className="text-white text-sm lg:text-base font-medium">Transform your ideas into powerful digital solutions</span>
                  </div>

                  {/* Main Headline */}
                  <h2 className="text-3xl lg:text-4xl xl:text-5xl 2xl:text-6xl font-bold text-white mb-4 lg:mb-5 xl:mb-6 leading-tight text-center px-4 lg:px-6">
                    Build <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00d690] to-[#00b876]">Exceptional Software</span><br />
                    with Expert Development Team
                  </h2>

                  {/* Description */}
                  <p className="text-base lg:text-lg xl:text-xl text-gray-300 max-w-2xl lg:max-w-3xl xl:max-w-4xl mx-auto mb-5 lg:mb-6 xl:mb-7 leading-relaxed text-center px-4 lg:px-6">
                    OctalChip delivers cutting-edge software solutions with our skilled team of developers, designers, and engineers who bring your vision to life.
                  </p>

                  {/* CTA Buttons */}
                  <div className="flex flex-row gap-3 lg:gap-4 xl:gap-5 justify-center px-4 lg:px-6">
                    <button
                      onClick={handleContactForm}
                      className="bg-gradient-to-r from-[#00d690] to-[#00b876] text-white px-6 lg:px-7 xl:px-8 py-3 lg:py-3.5 xl:py-4 rounded-lg text-sm lg:text-base xl:text-lg font-semibold hover:from-[#00b876] hover:to-[#00a066] transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-[#00d690]/25"
                    >
                      Start Your Project
                    </button>
                    <button
                      onClick={() => navigateToPage('/services')}
                      className="bg-transparent border-2 border-[#00d690] text-[#00d690] px-6 lg:px-7 xl:px-8 py-3 lg:py-3.5 xl:py-4 rounded-lg text-sm lg:text-base xl:text-lg font-semibold hover:bg-[#00d690] hover:text-white transition-all duration-300 backdrop-blur-sm"
                    >
                      Explore Services
                    </button>
                  </div>
                </div>

                {/* Floating cards around the oval */}
                <div className="absolute inset-0 pointer-events-none">
                  {/* Connection dots */}
                  <div className="absolute top-1/4 left-1/4 w-2.5 lg:w-3 xl:w-3.5 bg-[#00d690] rounded-full animate-pulse shadow-lg shadow-[#00d690]/50"></div>
                  <div className="absolute top-1/4 right-1/4 w-2.5 lg:w-3 xl:w-3.5 bg-[#00d690] rounded-full animate-pulse delay-300 shadow-lg shadow-[#00d690]/50"></div>
                  <div className="absolute bottom-1/4 left-1/4 w-2.5 lg:w-3 xl:w-3.5 bg-[#00d690] rounded-full animate-pulse delay-600 shadow-lg shadow-[#00d690]/50"></div>
                  <div className="absolute bottom-1/4 right-1/4 w-2.5 lg:w-3 xl:w-3.5 bg-[#00d690] rounded-full animate-pulse delay-900 shadow-lg shadow-[#00d690]/50"></div>

                  {/* Top-left floating card */}
                  <div className="absolute top-4 lg:top-5 xl:top-6 left-4 lg:left-5 xl:left-6 w-40 lg:w-44 xl:w-48 2xl:w-52 h-auto min-h-[100px] lg:min-h-[110px] xl:min-h-[120px] 2xl:min-h-[130px] bg-gray-800/90 border border-[#00d690]/60 rounded-xl p-3 lg:p-3.5 xl:p-4 backdrop-blur-sm hover:border-[#00d690] transition-all duration-300 hover:shadow-xl hover:shadow-[#00d690]/30 pointer-events-auto overflow-hidden">
                    <div className="flex items-start mb-2.5 lg:mb-3">
                      <div className="w-10 lg:w-11 xl:w-12 2xl:w-14 h-10 lg:h-11 xl:h-12 2xl:h-14 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-lg flex items-center justify-center mr-2.5 lg:mr-3 shadow-lg flex-shrink-0">
                        <Icon name="Code" size={20} className="lg:w-5 lg:h-5 xl:w-6 xl:h-6 2xl:w-7 2xl:h-7 text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-white font-semibold text-sm lg:text-base leading-snug mb-1 whitespace-normal">Full-Stack Development</h3>
                        <p className="text-gray-400 text-xs lg:text-sm leading-tight whitespace-normal">Web Applications</p>
                      </div>
                    </div>
                    <div className="text-gray-300 text-xs lg:text-sm leading-relaxed mt-2" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>End-to-end applications with modern frameworks and technologies</div>
                  </div>

                  {/* Top-right floating card */}
                  <div className="absolute top-4 lg:top-5 xl:top-6 right-4 lg:right-5 xl:right-6 w-40 lg:w-44 xl:w-48 2xl:w-52 h-auto min-h-[100px] lg:min-h-[110px] xl:min-h-[120px] 2xl:min-h-[130px] bg-gray-800/90 border border-[#00d690]/60 rounded-xl p-3 lg:p-3.5 xl:p-4 backdrop-blur-sm hover:border-[#00d690] transition-all duration-300 hover:shadow-xl hover:shadow-[#00d690]/30 pointer-events-auto overflow-hidden">
                    <div className="flex items-start mb-2.5 lg:mb-3">
                      <div className="w-10 lg:w-11 xl:w-12 2xl:w-14 h-10 lg:h-11 xl:h-12 2xl:h-14 bg-gradient-to-br from-[#00d690]/80 to-[#00b876] rounded-lg flex items-center justify-center mr-2.5 lg:mr-3 shadow-lg flex-shrink-0">
                        <Icon name="Smartphone" size={20} className="lg:w-5 lg:h-5 xl:w-6 xl:h-6 2xl:w-7 2xl:h-7 text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-white font-semibold text-sm lg:text-base leading-snug mb-1 whitespace-normal">Mobile Development</h3>
                        <p className="text-gray-400 text-xs lg:text-sm leading-tight whitespace-normal">iOS & Android</p>
                      </div>
                    </div>
                    <div className="text-gray-300 text-xs lg:text-sm leading-relaxed mt-2" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>Native and cross-platform mobile solutions for all devices</div>
                  </div>

                  {/* Bottom-left floating card */}
                  <div className="absolute bottom-4 lg:bottom-5 xl:bottom-6 left-4 lg:left-5 xl:left-6 w-40 lg:w-44 xl:w-48 2xl:w-52 h-auto min-h-[100px] lg:min-h-[110px] xl:min-h-[120px] 2xl:min-h-[130px] bg-gray-800/90 border border-[#00d690]/60 rounded-xl p-3 lg:p-3.5 xl:p-4 backdrop-blur-sm hover:border-[#00d690] transition-all duration-300 hover:shadow-xl hover:shadow-[#00d690]/30 pointer-events-auto overflow-hidden">
                    <div className="flex items-start mb-2.5 lg:mb-3">
                      <div className="w-10 lg:w-11 xl:w-12 2xl:w-14 h-10 lg:h-11 xl:h-12 2xl:h-14 bg-gradient-to-br from-[#00b876] to-[#00a066] rounded-lg flex items-center justify-center mr-2.5 lg:mr-3 shadow-lg flex-shrink-0">
                        <Icon name="Cloud" size={20} className="lg:w-5 lg:h-5 xl:w-6 xl:h-6 2xl:w-7 2xl:h-7 text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-white font-semibold text-sm lg:text-base leading-snug mb-1 whitespace-normal">Cloud & DevOps</h3>
                        <p className="text-gray-400 text-xs lg:text-sm leading-tight whitespace-normal">Infrastructure</p>
                      </div>
                    </div>
                    <div className="text-gray-300 text-xs lg:text-sm leading-relaxed mt-2" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>Scalable cloud infrastructure and automated deployment</div>
                  </div>

                  {/* Bottom-right floating card */}
                  <div className="absolute bottom-4 lg:bottom-5 xl:bottom-6 right-4 lg:right-5 xl:right-6 w-40 lg:w-44 xl:w-48 2xl:w-52 h-auto min-h-[100px] lg:min-h-[110px] xl:min-h-[120px] 2xl:min-h-[130px] bg-gray-800/90 border border-[#00d690]/60 rounded-xl p-3 lg:p-3.5 xl:p-4 backdrop-blur-sm hover:border-[#00d690] transition-all duration-300 hover:shadow-xl hover:shadow-[#00d690]/30 pointer-events-auto overflow-hidden">
                    <div className="flex items-start mb-2.5 lg:mb-3">
                      <div className="w-10 lg:w-11 xl:w-12 2xl:w-14 h-10 lg:h-11 xl:h-12 2xl:h-14 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-lg flex items-center justify-center mr-2.5 lg:mr-3 shadow-lg flex-shrink-0">
                        <Icon name="Zap" size={20} className="lg:w-5 lg:h-5 xl:w-6 xl:h-6 2xl:w-7 2xl:h-7 text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-white font-semibold text-sm lg:text-base leading-snug mb-1 whitespace-normal">AI & ML Solutions</h3>
                        <p className="text-gray-400 text-xs lg:text-sm leading-tight whitespace-normal">Intelligent Systems</p>
                      </div>
                    </div>
                    <div className="text-gray-300 text-xs lg:text-sm leading-relaxed mt-2" style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>AI-powered applications and machine learning solutions</div>
                  </div>
                </div>

                {/* Animated connecting lines */}
                <div className="absolute inset-0 pointer-events-none">
                  {/* Lines connecting dots to center with stronger visibility */}
                  <div className="absolute top-1/2 left-1/2 w-24 lg:w-28 xl:w-32 2xl:w-40 h-px bg-gradient-to-r from-[#00d690]/60 via-[#00d690]/40 to-transparent transform -translate-x-1/2 -translate-y-1/2 rotate-45 animate-pulse"></div>
                  <div className="absolute top-1/2 left-1/2 w-24 lg:w-28 xl:w-32 2xl:w-40 h-px bg-gradient-to-r from-[#00d690]/60 via-[#00d690]/40 to-transparent transform -translate-x-1/2 -translate-y-1/2 -rotate-45 animate-pulse delay-300"></div>
                  <div className="absolute top-1/2 left-1/2 w-24 lg:w-28 xl:w-32 2xl:w-40 h-px bg-gradient-to-r from-[#00d690]/60 via-[#00d690]/40 to-transparent transform translate-x-1/2 -translate-y-1/2 rotate-45 animate-pulse delay-600"></div>
                  <div className="absolute top-1/2 left-1/2 w-24 lg:w-28 xl:w-32 2xl:w-40 h-px bg-gradient-to-r from-[#00d690]/60 via-[#00d690]/40 to-transparent transform translate-x-1/2 -translate-y-1/2 -rotate-45 animate-pulse delay-900"></div>

                  {/* Additional network lines for more complexity */}
                  <div className="absolute top-1/3 left-1/3 w-16 lg:w-18 xl:w-20 2xl:w-24 h-px bg-gradient-to-r from-[#00b876]/40 to-transparent transform rotate-12 animate-pulse delay-200"></div>
                  <div className="absolute bottom-1/3 right-1/3 w-16 lg:w-18 xl:w-20 2xl:w-24 h-px bg-gradient-to-r from-[#00b876]/40 to-transparent transform -rotate-12 animate-pulse delay-500"></div>
                  <div className="absolute top-2/3 left-2/3 w-12 lg:w-14 xl:w-16 2xl:w-20 h-px bg-gradient-to-r from-[#00d690]/30 to-transparent transform rotate-45 animate-pulse delay-700"></div>
                  <div className="absolute bottom-2/3 right-2/3 w-12 lg:w-14 xl:w-16 2xl:w-20 h-px bg-gradient-to-r from-[#00d690]/30 to-transparent transform -rotate-45 animate-pulse delay-1000"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hero Section - Mobile Responsive */}
      <section id="hero-mobile" className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4 md:mb-6 leading-tight">
              We are Digital Expert for Web, mobile, cloud, backend, and AI/ML developers
            </h1>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 mb-6 md:mb-8 leading-relaxed max-w-3xl mx-auto">
              As a cutting-edge digital agency, our design philosophy goes beyond aesthetics.
              We blend creativity, functionality, and strategy to deliver visually stunning and user-centric solutions.
            </p>
            <button
              className="bg-[#00d690] hover:bg-[#00b876] text-white px-6 py-3 md:px-8 md:py-4 rounded-lg text-sm md:text-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-[#00d690]/25"
              onClick={handleContactForm}
            >
              Get Started
            </button>
          </div>
        </div>
      </section>

      {/* Stats Section - Mobile Responsive */}
      <section id="stats" className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">
              Your Tech-Driven Digital Innovation Partner
            </h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              As your trusted tech partner, OctalChip delivers innovative web, mobile, cloud, backend, and AI/ML solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 max-w-6xl mx-auto">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="bg-white p-4 md:p-6 lg:p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-center border border-gray-100 hover:border-[#00d690]/20"
              >
                <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#00d690] mb-2 md:mb-3">
                  {stat.number}
                </div>
                <h3 className="text-sm md:text-base lg:text-lg font-semibold text-gray-800 leading-tight">
                  {stat.label}
                </h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">What We Offer</h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive digital solutions designed to transform your business and drive success
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover-lift group relative overflow-hidden cursor-pointer"
                style={{ borderRadius: '12px' }}
                onClick={() => navigateToPage(service.path)}
              >
                {/* Gradient overlay on hover */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-in-out"
                  style={{
                    background: 'linear-gradient(45deg, #00dc88, #00bcd4)',
                    borderRadius: '14px',
                    top: '-2px',
                    left: '-2px',
                    right: '-2px',
                    bottom: '-2px',
                    zIndex: '-1'
                  }}
                ></div>

                <div className="relative z-10">
                  <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-white/20 transition-all duration-300">
                    <Icon
                      name={service.icon}
                      size={32}
                      className="text-[#00d690] group-hover:text-white transition-all duration-300"
                    />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-800 group-hover:text-white mb-4 transition-all duration-300">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 group-hover:text-white/90 leading-relaxed transition-all duration-300">
                    {service.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button
              variant="primary"
              onClick={() => navigateToPage('/services')}
              className="px-6 sm:px-8 py-2.5 sm:py-3 text-sm sm:text-base md:text-lg group relative overflow-hidden transform transition-all duration-300 hover:scale-110 hover:shadow-2xl hover:shadow-[#00d690]/50 active:scale-105"
              iconName="ChevronRight"
              iconPosition="right"
              size="lg"
            >
              View All Services
            </Button>
          </div>
        </div>
      </section>

      {/* AI & ML Services Section */}
      <section id="ai-ml-services" className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">AI & ML Services</h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              Transform your business with cutting-edge Artificial Intelligence and Machine Learning solutions. From machine learning models to intelligent chatbots, we deliver AI-powered innovations that drive growth.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {aiMLServices.map((service, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover-lift group relative overflow-hidden cursor-pointer"
                style={{ borderRadius: '12px' }}
                onClick={() => navigateToPage(service.path)}
              >
                {/* Gradient overlay on hover */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-in-out"
                  style={{
                    background: 'linear-gradient(45deg, #00dc88, #00bcd4)',
                    borderRadius: '14px',
                    top: '-2px',
                    left: '-2px',
                    right: '-2px',
                    bottom: '-2px',
                    zIndex: '-1'
                  }}
                ></div>

                <div className="relative z-10">
                  <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-white/20 transition-all duration-300">
                    <Icon
                      name={service.icon}
                      size={32}
                      className="text-[#00d690] group-hover:text-white transition-all duration-300"
                    />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-800 group-hover:text-white mb-4 transition-all duration-300">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 group-hover:text-white/90 leading-relaxed transition-all duration-300">
                    {service.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button
              variant="primary"
              onClick={() => navigateToPage('/ai-ml-services')}
              className="px-6 sm:px-8 py-2.5 sm:py-3 text-sm sm:text-base md:text-lg group relative overflow-hidden transform transition-all duration-300 hover:scale-110 hover:shadow-2xl hover:shadow-[#00d690]/50 active:scale-105"
              iconName="ChevronRight"
              iconPosition="right"
              size="lg"
            >
              View AI & ML Services
            </Button>
          </div>
        </div>
      </section>

      {/* Automation & Integrations Section */}
      <section id="automation-integrations" className="py-16 md:py-24 lg:py-28 relative overflow-hidden bg-gradient-to-br from-gray-50 via-white to-[#00d690]/5">
        {/* Background Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#00d690]/5 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#00b876]/5 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2"></div>
        
        {/* Diagonal Accent Lines */}
        <div className="absolute top-20 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#00d690]/20 to-transparent transform rotate-12 hidden lg:block"></div>
        <div className="absolute bottom-20 right-0 w-full h-px bg-gradient-to-l from-transparent via-[#00b876]/20 to-transparent transform -rotate-12 hidden lg:block"></div>

        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00d690]/10 rounded-full border border-[#00d690]/20 mb-6">
              <div className="w-2 h-2 bg-[#00d690] rounded-full animate-pulse"></div>
              <span className="text-sm font-semibold text-[#00d690]">Workflow Automation</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
              Automation & Integrations
            </h2>
            <div className="w-24 md:w-32 h-1 bg-gradient-to-r from-[#00d690] to-[#00b876] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl lg:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              We help businesses automate workflows and connect applications using n8n, Zapier, and Make. 
              Eliminate manual work and improve operational efficiency with intelligent automation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-7xl mx-auto mb-12">
            {automationServices.map((service, index) => (
              <div 
                key={index}
                className="group relative bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-4 cursor-pointer overflow-hidden border border-gray-100 flex flex-col h-full"
                onClick={() => navigateToPage(service.path)}
              >
                {/* Top Chevron Shape with Icon */}
                <div className={`relative h-20 bg-gradient-to-br ${service.gradient} flex items-center justify-center flex-shrink-0`}>
                  {/* Chevron Shape */}
                  <div className={`absolute -bottom-8 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[30px] border-l-transparent border-r-[30px] border-r-transparent border-t-[30px] ${service.chevronColor} ${service.chevronHoverColor} transition-colors duration-300`}></div>
                  {/* Icon Container */}
                  <div className="relative z-10 w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-lg transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                    <Icon name={service.icon} size={28} className="text-[#00d690]" />
                  </div>
                </div>

                {/* Card Content - Flex Column to Push Button to Bottom */}
                <div className="relative z-10 pt-10 pb-8 px-6 md:px-8 flex flex-col flex-grow">
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3 text-center">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-6 text-center text-sm md:text-base h-16">
                    {service.description}
                  </p>
                  
                  {/* Features List - Single Row */}
                  <div className="flex flex-wrap gap-2 mb-6 justify-center">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-1.5 px-2.5 py-1 bg-[#00d690]/10 rounded-full border border-[#00d690]/20">
                        <div className="w-1.5 h-1.5 bg-[#00d690] rounded-full flex-shrink-0"></div>
                        <span className="text-xs text-gray-700 font-medium whitespace-nowrap">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <button
                    className="w-full bg-gradient-to-r from-[#00d690] to-[#00b876] hover:from-[#00b876] hover:to-[#00d690] text-white font-bold py-3.5 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl mt-auto"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigateToPage(service.path);
                    }}
                  >
                    Explore {service.title}
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Button
              variant="outline"
              onClick={() => navigateToPage('/services/workflow-automation#what-we-offer')}
              className="px-8 md:px-10 py-3 md:py-4 text-base md:text-lg border-2 border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white font-bold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              iconName="ChevronRight"
              iconPosition="right"
              size="lg"
            >
              View All Automation Services
            </Button>
          </div>
        </div>
      </section>

      {/* AI/ML Development Process Timeline */}
      <section id="ai-ml-process" className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">AI/ML Development Journey</h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              From data discovery to model deployment, we follow a comprehensive AI/ML methodology that ensures accuracy, scalability, and business value
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-[#00d690]"></div>

              {aiMLDevelopmentTimeline.map((step, index) => (
                <div key={index} className="relative flex items-start mb-12">
                  {/* Timeline Dot */}
                  <div className="absolute left-6 w-4 h-4 bg-[#00d690] rounded-full border-4 border-white shadow-lg"></div>

                  {/* Content */}
                  <div className="ml-16 bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-[#00d690]/10 rounded-full flex items-center justify-center text-xl mr-4">
                        {step.icon}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-800">{step.phase}</h3>
                        <p className="text-[#00d690] font-semibold">{step.duration}</p>
                      </div>
                    </div>

                    <p className="text-gray-600 mb-4">{step.description}</p>

                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2">Key Deliverables:</h4>
                      <div className="flex flex-wrap gap-2">
                        {step.deliverables.map((deliverable, idx) => (
                          <span key={idx} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                            {deliverable}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="why-choose-us" className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">Why to Choose Us</h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
          </div>

          <div className="flex flex-col lg:flex-row items-center gap-12">
            {/* Left Side - Cards Grid */}
            <div className="lg:w-1/2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {whyChooseUs.map((item, index) => (
                  <div
                    key={index}
                    className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover-lift group relative overflow-hidden"
                    style={{
                      borderRadius: '12px'
                    }}
                  >
                    {/* Gradient overlay on hover */}
                    <div
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-in-out"
                      style={{
                        background: 'linear-gradient(45deg, #00dc88, #00bcd4)',
                        borderRadius: '14px',
                        top: '-2px',
                        left: '-2px',
                        right: '-2px',
                        bottom: '-2px',
                        zIndex: '-1'
                      }}
                    ></div>

                    <div className="relative z-10">
                      <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-white/20 transition-all duration-300">
                        <Icon
                          name={item.icon}
                          size={24}
                          className="text-[#00d690] group-hover:text-white transition-all duration-300"
                        />
                      </div>
                      <h3 className="text-lg font-bold text-gray-800 group-hover:text-white mb-3 transition-all duration-300">
                        {item.title}
                      </h3>
                      <p className="text-gray-600 group-hover:text-white/90 text-sm leading-relaxed transition-all duration-300">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Side - Image */}
            <div className="lg:w-1/2">
              <div className="bg-gradient-to-br from-[#00d690]/10 to-[#00d690]/5 rounded-2xl p-8 h-96 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-24 h-24 bg-[#00d690]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Icon name="Users" size={48} className="text-[#00d690]" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">Our Team at Work</h3>
                  <p className="text-gray-600 max-w-md mx-auto">
                    Our dedicated team of developers and designers working collaboratively to deliver exceptional digital solutions for our clients.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Mobile Responsive */}
      <section id="testimonials" className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-800 mb-4">What Our Clients Say</h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4"></div>
            <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Don't just take our word for it. Here's what our satisfied clients have to say about working with OctalChip.
            </p>
          </div>

          {/* Mobile Layout - Simple Grid */}
          <div className="block md:hidden">
            <div className="grid grid-cols-1 gap-4">
              {testimonials.slice(0, 3).map((testimonial, index) => (
                <div key={`mobile-${index}`} className="bg-white rounded-xl shadow-lg p-4 border border-gray-100">
                  {/* Company Logo and Description */}
                  <div className="mb-3">
                    <div className="flex items-center mb-2">
                      <div className="w-6 h-6 bg-[#00d690] rounded-lg flex items-center justify-center mr-2">
                        <span className="text-white font-bold text-xs">
                          {testimonial.companyLogo.charAt(0)}
                        </span>
                      </div>
                      <span className="font-semibold text-gray-800 text-sm">
                        {testimonial.company}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 ml-8">
                      {testimonial.companyDescription}
                    </p>
                  </div>

                  {/* Testimonial Quote */}
                  <div className="mb-4">
                    <p className="text-gray-700 leading-relaxed italic text-sm">
                      "{testimonial.description}"
                    </p>
                  </div>

                  {/* Client Info */}
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                      {testimonial.avatar}
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-800 text-sm">
                        {testimonial.name}
                      </h4>
                      <p className="text-xs text-gray-600">
                        {testimonial.jobTitle} at {testimonial.company}
                      </p>
                      {/* Rating */}
                      <div className="flex mt-1">
                        {[...Array(5)].map((_, i) => (
                          <Icon
                            key={i}
                            name="Star"
                            size={12}
                            className={`mr-0.5 ${i < testimonial.rating ? 'text-[#00d690] fill-current' : 'text-gray-300'}`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile CTA */}
            <div className="text-center mt-6">
              <button
                onClick={handleContactForm}
                className="bg-[#00d690] hover:bg-[#00b876] text-white px-6 py-2 rounded-lg text-sm font-semibold transition-all duration-300"
              >
                Get Started Today
              </button>
            </div>
          </div>

          {/* Desktop Layout - Infinite Scroll */}
          <div className="hidden md:block">
            <div className="relative overflow-hidden">
              <div
                className="flex"
                style={{
                  transform: `translateX(-${testimonialScrollPosition}px)`,
                  width: `${testimonials.length * 2 * 400}px` // Double width for seamless loop
                }}
                onMouseEnter={() => setIsTestimonialPaused(true)}
                onMouseLeave={() => setIsTestimonialPaused(false)}
              >
                {/* First set of testimonials */}
                {testimonials.map((testimonial, index) => (
                  <div key={`first-${index}`} className="flex-shrink-0 w-[400px] p-4">
                    <div className="bg-white rounded-xl shadow-lg p-6 h-full border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                      {/* Company Logo and Description */}
                      <div className="mb-4">
                        <div className="flex items-center mb-2">
                          <div className="w-8 h-8 bg-[#00d690] rounded-lg flex items-center justify-center mr-3">
                            <span className="text-white font-bold text-sm">
                              {testimonial.companyLogo.charAt(0)}
                            </span>
                          </div>
                          <span className="font-semibold text-gray-800 text-lg">
                            {testimonial.company}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 ml-11">
                          {testimonial.companyDescription}
                        </p>
                      </div>

                      {/* Testimonial Quote */}
                      <div className="mb-6">
                        <p className="text-gray-700 leading-relaxed italic">
                          "{testimonial.description}"
                        </p>
                      </div>

                      {/* Client Info */}
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mr-4">
                          {testimonial.avatar}
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-800">
                            {testimonial.name}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {testimonial.jobTitle} at {testimonial.company}
                          </p>
                          {/* Rating */}
                          <div className="flex mt-1">
                            {[...Array(5)].map((_, i) => (
                              <Icon
                                key={i}
                                name="Star"
                                size={16}
                                className={`mr-1 ${i < testimonial.rating ? 'text-[#00d690] fill-current' : 'text-gray-300'}`}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Duplicate set for seamless loop */}
                {testimonials.map((testimonial, index) => (
                  <div key={`second-${index}`} className="flex-shrink-0 w-[400px] p-4">
                    <div className="bg-white rounded-xl shadow-lg p-6 h-full border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                      {/* Company Logo and Description */}
                      <div className="mb-4">
                        <div className="flex items-center mb-2">
                          <div className="w-8 h-8 bg-[#00d690] rounded-lg flex items-center justify-center mr-3">
                            <span className="text-white font-bold text-sm">
                              {testimonial.companyLogo.charAt(0)}
                            </span>
                          </div>
                          <span className="font-semibold text-gray-800 text-lg">
                            {testimonial.company}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 ml-11">
                          {testimonial.companyDescription}
                        </p>
                      </div>

                      {/* Testimonial Quote */}
                      <div className="mb-6">
                        <p className="text-gray-700 leading-relaxed italic">
                          "{testimonial.description}"
                        </p>
                      </div>

                      {/* Client Info */}
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mr-4">
                          {testimonial.avatar}
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-800">
                            {testimonial.name}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {testimonial.jobTitle} at {testimonial.company}
                          </p>
                          {/* Rating */}
                          <div className="flex mt-1">
                            {[...Array(5)].map((_, i) => (
                              <Icon
                                key={i}
                                name="Star"
                                size={16}
                                className={`mr-1 ${i < testimonial.rating ? 'text-[#00d690] fill-current' : 'text-gray-300'}`}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust & Reviews Section */}
      <section id="trust-reviews" className="py-12 md:py-20 bg-gradient-to-br from-[#00d690]/5 to-[#00d690]/10">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-8 md:mb-12">
              Trusted by Leading Companies Worldwide
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Clutch.co Column */}
              <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg">
                <div className="flex flex-col items-center justify-between gap-6 h-full">
                  {/* Clutch Logo and Rating */}
                  <div className="text-center">
                    <div className="text-4xl md:text-5xl font-bold text-gray-800 mb-2">
                      Clutch
                      <span className="inline-block w-3 h-3 bg-red-500 rounded-full ml-1"></span>
                    </div>
                    <div className="flex items-center justify-center gap-2 mb-4">
                      <span className="text-3xl md:text-4xl font-bold text-gray-800">4.9</span>
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <Icon
                            key={i}
                            name="Star"
                            size={24}
                            className={`mx-0.5 ${i < 4 ? 'text-[#00d690] fill-current' : i === 4 ? 'text-[#00d690] fill-current opacity-90' : 'text-gray-300'}`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Trust Statement */}
                  <div className="text-center">
                    <p className="text-xl md:text-2xl text-gray-800 font-semibold mb-4">
                      Hundreds of companies trust OctalChip for their development needs
                    </p>
                    <p className="text-gray-600 text-lg mb-6">
                      Verified reviews from real clients on Clutch.co
                    </p>
                  </div>

                  {/* Spacer */}
                  <div className="flex-grow"></div>

                  {/* CTA Button */}
                  <a
                    href="https://clutch.co/profile/octalchip#reviews"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center bg-[#00d690] hover:bg-[#00b876] text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105"
                  >
                    <Icon name="ExternalLink" size={18} className="mr-2" />
                    View Clutch.co Profile
                  </a>
                </div>
              </div>

              {/* Trustpilot Column */}
              <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg">
                <div className="flex flex-col items-center justify-between gap-6 h-full">
                  {/* Trustpilot Logo and Rating */}
                  <div className="text-center">
                    <div className="text-4xl md:text-5xl font-bold text-gray-800 mb-2 flex items-center justify-center gap-2">
                      <Icon
                        name="Star"
                        size={48}
                        className="text-[#00d690] fill-current"
                      />
                      Trustpilot
                    </div>
                    <div className="flex items-center justify-center gap-2 mb-4">
                      <span className="text-3xl md:text-4xl font-bold text-gray-800">4.7</span>
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <Icon
                            key={i}
                            name="Star"
                            size={24}
                            className={`mx-0.5 ${i < 4 ? 'text-[#00d690] fill-current' : i === 4 ? 'text-[#00d690] fill-current opacity-90' : 'text-gray-300'}`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Trust Statement */}
                  <div className="text-center">
                    <p className="text-xl md:text-2xl text-gray-800 font-semibold mb-4">
                      Real customer experiences and verified reviews
                    </p>
                    <p className="text-gray-600 text-lg mb-6">
                      See what our clients say about our services
                    </p>
                  </div>

                  {/* Spacer */}
                  <div className="flex-grow"></div>

                  {/* CTA Button */}
                  <a
                    href="https://www.trustpilot.com/review/octalchip.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center bg-[#00d690] hover:bg-[#00b876] text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105"
                  >
                    <Icon name="ExternalLink" size={18} className="mr-2" />
                    View Trustpilot Reviews
                  </a>
                </div>
              </div>

              {/* G2 Column */}
              <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg">
                <div className="flex flex-col items-center justify-between gap-6 h-full">
                  {/* G2 Logo and Rating */}
                  <div className="text-center">
                    <div className="text-4xl md:text-5xl font-bold text-gray-800 mb-2 flex items-center justify-center gap-2">
                      G2
                    </div>
                    <div className="flex items-center justify-center gap-2 mb-4">
                      <span className="text-3xl md:text-4xl font-bold text-gray-800">4.5</span>
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <Icon
                            key={i}
                            name="Star"
                            size={24}
                            className={`mx-0.5 ${i < 4 ? 'text-[#00d690] fill-current' : i === 4 ? 'text-[#00d690] fill-current opacity-90' : 'text-gray-300'}`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Trust Statement */}
                  <div className="text-center">
                    <p className="text-xl md:text-2xl text-gray-800 font-semibold mb-4">
                      Comprehensive software reviews and ratings
                    </p>
                    <p className="text-gray-600 text-lg mb-6">
                      Read authentic reviews from verified users on G2
                    </p>
                  </div>

                  {/* Spacer */}
                  <div className="flex-grow"></div>

                  {/* CTA Button */}
                  <a
                    href="https://www.g2.com/products/octalchip/reviews"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center bg-[#00d690] hover:bg-[#00b876] text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105"
                  >
                    <Icon name="ExternalLink" size={18} className="mr-2" />
                    View G2 Reviews
                  </a>
                </div>
              </div>

              {/* GoodFirms Column */}
              <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg">
                <div className="flex flex-col items-center justify-between gap-6 h-full">
                  {/* GoodFirms Logo and Rating */}
                  <div className="text-center">
                    <div className="text-4xl md:text-5xl font-bold text-gray-800 mb-2 flex items-center justify-center gap-2">
                      GoodFirms
                    </div>
                    <div className="flex items-center justify-center gap-2 mb-4">
                      <span className="text-3xl md:text-4xl font-bold text-gray-800">4.8</span>
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <Icon
                            key={i}
                            name="Star"
                            size={24}
                            className={`mx-0.5 ${i < 4 ? 'text-[#00d690] fill-current' : i === 4 ? 'text-[#00d690] fill-current opacity-90' : 'text-gray-300'}`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Trust Statement */}
                  <div className="text-center">
                    <p className="text-xl md:text-2xl text-gray-800 font-semibold mb-4">
                      Top-rated development company on GoodFirms
                    </p>
                    <p className="text-gray-600 text-lg mb-6">
                      Discover why businesses choose OctalChip for excellence
                    </p>
                  </div>

                  {/* Spacer */}
                  <div className="flex-grow"></div>

                  {/* CTA Button */}
                  <a
                    href="https://www.goodfirms.co/company/octalchip"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center bg-[#00d690] hover:bg-[#00b876] text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105"
                  >
                    <Icon name="ExternalLink" size={18} className="mr-2" />
                    View GoodFirms Profile
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Blogs Section */}
      <section id="recent-blogs" className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">Latest Insights & Trends</h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              Stay updated with our latest insights on web development, mobile apps, AI integration,
              and DevOps best practices from our expert development team.
            </p>
          </div>

          {isLoadingBlogs ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#00d690]"></div>
              <p className="mt-4 text-gray-600">Loading latest articles...</p>
            </div>
          ) : recentBlogs.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {recentBlogs.map((blog) => (
                <Link
                  key={blog.id}
                  to={`/blog/${blog.slug}`}
                  className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover-lift group relative overflow-hidden border border-gray-100"
                  style={{ borderRadius: '12px' }}
                >

                  <div className="p-6">
                    {/* Category and Read Time */}
                    <div className="flex gap-2 mb-4 flex-wrap">
                      <span className="bg-[#00d690] text-white px-3 py-1 rounded-full text-xs font-semibold">
                        {blog.category}
                      </span>
                      <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-xs font-medium">
                        {blog.readTime}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-[#00d690] transition-colors line-clamp-2">
                      {blog.title}
                    </h3>

                    {/* Description */}
                    <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">
                      {blog.shortDescription}
                    </p>

                    {/* Date and Read Time */}
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                      <div className="flex items-center gap-2">
                        <Icon name="Calendar" size={16} />
                        <span>{formatDate(blog.publishDate)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Icon name="Clock" size={16} />
                        <span>{blog.readTime}</span>
                      </div>
                    </div>

                    {/* Tags */}
                    <div className="flex gap-1 mb-4 flex-wrap">
                      {blog.tags.slice(0, 2).map((tag, index) => (
                        <span
                          key={index}
                          className="bg-gray-50 text-gray-600 px-2 py-1 rounded text-xs font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                      {blog.tags.length > 2 && (
                        <span className="bg-gray-50 text-gray-600 px-2 py-1 rounded text-xs font-medium">
                          +{blog.tags.length - 2} more
                        </span>
                      )}
                    </div>

                    {/* Read More Button */}
                    <div className="text-center">
                      <span className="inline-flex items-center text-[#00d690] font-semibold text-sm group-hover:text-[#00b876] transition-colors">
                        Read More
                        <Icon name="ArrowRight" size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Icon name="BookOpen" size={48} className="text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">No articles available at the moment.</p>
            </div>
          )}

          {/* View All Blogs Button */}
          <div className="text-center">
            <Link
              to="/blog"
              className="inline-flex items-center bg-[#00d690] hover:bg-[#00b876] text-white font-semibold py-3 px-8 rounded-lg transition-all duration-300 transform hover:scale-105"
            >
              <Icon name="BookOpen" size={20} className="mr-2" />
              View All Articles
            </Link>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">
              Creative Developers. Functional Solutions
            </h2>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              At Octalchip, our skilled developers bring creative design and technical expertise together
              to build intuitive, high-quality digital products that prioritize user experience and performance.
            </p>
          </div>

          <div className="text-center mb-12">
            <h3 className="text-2xl font-bold text-text-primary mb-8">Our Tech Stack</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h4 className="text-lg font-semibold text-text-primary mb-4">Front End</h4>
                <div className="flex flex-wrap justify-center gap-2">
                  {techStack.frontend.map((tech, index) => (
                    <span key={index} className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-text-primary mb-4">Backend</h4>
                <div className="flex flex-wrap justify-center gap-2">
                  {techStack.backend.map((tech, index) => (
                    <span key={index} className="bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full text-sm">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-text-primary mb-4">Cloud</h4>
                <div className="flex flex-wrap justify-center gap-2">
                  {techStack.cloud.map((tech, index) => (
                    <span key={index} className="bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full text-sm">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section id="industries" className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">Industries We Serve</h2>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              We are architects of innovation across industries. Our profound expertise in crafting
              industry-specific solutions redefines excellence.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
            {industries.map((industry, index) => (
              <div key={index} className="text-center group">
                <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:bg-primary group-hover:text-white transition-colors">
                  <Icon name={industry.icon} size={32} />
                </div>
                <h3 className="font-semibold text-text-primary">{industry.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">Our Development Process</h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              Our proven development methodology ensures your project is delivered on time, within budget,
              and exceeds your expectations with transparent communication throughout.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((process, index) => (
              <div key={index} className="text-center group">
                <div className="relative mb-6">
                  <div className="w-20 h-20 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#00d690] group-hover:text-white transition-all duration-300">
                    <span className="text-2xl font-bold text-[#00d690] group-hover:text-white">{process.step}</span>
                  </div>
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto absolute top-2 left-1/2 transform -translate-x-1/2 shadow-lg">
                    <Icon name={process.icon} size={24} className="text-[#00d690]" />
                  </div>
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-4">{process.title}</h3>
                <p className="text-gray-600 leading-relaxed">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section id="case-studies" className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">Our Success Stories</h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              Discover how we've helped businesses transform their digital presence and achieve remarkable results
              through innovative development solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {caseStudies.map((caseStudy, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
                <div className="p-8">
                  <div className="flex items-center mb-6">
                    <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mr-4 text-3xl">
                      {caseStudy.image}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">{caseStudy.title}</h3>
                      <p className="text-gray-600">{caseStudy.client} • {caseStudy.industry}</p>
                      <div className="flex gap-4 mt-2 text-sm text-gray-500">
                        <span><Icon name="Clock" size={16} className="inline mr-1" /> {caseStudy.duration}</span>
                        <span><Icon name="Users" size={16} className="inline mr-1" /> {caseStudy.team}</span>
                      </div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-800 mb-2">Challenge:</h4>
                    <p className="text-gray-600 mb-4">{caseStudy.challenge}</p>

                    <h4 className="font-semibold text-gray-800 mb-2">Solution:</h4>
                    <p className="text-gray-600 mb-4">{caseStudy.solution}</p>
                  </div>

                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-800 mb-3">Results:</h4>
                    <ul className="space-y-2">
                      {caseStudy.results.map((result, idx) => (
                        <li key={idx} className="flex items-center text-gray-600">
                          <Icon name="CheckCircle" size={16} className="text-[#00d690] mr-2" />
                          {result}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {caseStudy.technologies.map((tech, idx) => (
                      <span key={idx} className="bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full text-sm font-medium">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Expertise Section */}
      <section id="expertise" className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">Build Your Digital Future</h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              OctalChip delivers cutting-edge web applications, mobile apps, and cloud solutions
              that drive business growth and digital transformation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                className="bg-[#00d690] hover:bg-[#00b876] text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105"
                onClick={handleContactForm}
              >
                Start Your Project
              </button>
              <button
                className="border-2 border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white font-bold py-4 px-8 rounded-lg transition-all duration-300"
                onClick={() => navigateToPage('/product')}
              >
                View Our Portfolio
              </button>
            </div>
          </div>

          {/* Regular Services Section */}
          <div className="mb-16">
            <div className="text-center mb-8">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3">Development Services</h3>
              <div className="w-20 h-1 bg-[#00d690] mx-auto mb-4"></div>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Comprehensive development solutions for web, mobile, cloud, and backend systems
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {expertiseCards.filter(expertise => expertise.category === 'regular').map((expertise, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 group border border-gray-100">
                  <div className="text-center mb-4">
                    <div className="w-16 h-16 bg-[#00d690]/10 rounded-full flex items-center justify-center text-3xl mx-auto mb-4 group-hover:bg-[#00d690] group-hover:text-white transition-all duration-300">
                      {expertise.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-[#00d690] transition-colors">{expertise.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{expertise.description}</p>
                  </div>

                  <div className="mb-4">
                    <div className="flex flex-wrap gap-2 justify-center">
                      {expertise.technologies.map((tech, idx) => (
                        <span key={idx} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs hover:bg-[#00d690] hover:text-white transition-colors">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="text-center space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Projects:</span>
                      <span className="text-[#00d690] font-semibold">{expertise.projects}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Clients:</span>
                      <span className="text-gray-700 font-medium">{expertise.clients}</span>
                    </div>
                  </div>

                  <div className="text-center">
                    <button
                      className="bg-[#00d690] hover:bg-[#00b876] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 w-full"
                      onClick={() => navigateToPage(expertise.path)}
                    >
                      Learn More
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-12">
              <button
                className="bg-white border-2 border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white font-semibold py-3 px-8 rounded-lg transition-all duration-300"
                onClick={() => navigateToPage('/services')}
              >
                Explore All Development Services
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* AI & Machine Learning Services Section */}
      <section id="ai-ml-expertise" className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">AI & Machine Learning Services</h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Transform your business with cutting-edge Artificial Intelligence and Machine Learning solutions. 
              From machine learning models to intelligent chatbots, we deliver AI-powered innovations that drive growth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                className="bg-[#00d690] hover:bg-[#00b876] text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105"
                onClick={handleContactForm}
              >
                Start Your AI Project
              </button>
              <button
                className="border-2 border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white font-bold py-4 px-8 rounded-lg transition-all duration-300"
                onClick={() => navigateToPage('/ai-ml-services')}
              >
                View AI/ML Services
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {expertiseCards.filter(expertise => expertise.category === 'ai-ml').map((expertise, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 group border border-gray-100">
                <div className="text-center mb-4">
                  <div className="w-16 h-16 bg-[#00d690]/10 rounded-full flex items-center justify-center text-3xl mx-auto mb-4 group-hover:bg-[#00d690] group-hover:text-white transition-all duration-300">
                    {expertise.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-[#00d690] transition-colors">{expertise.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{expertise.description}</p>
                </div>

                <div className="mb-4">
                  <div className="flex flex-wrap gap-2 justify-center">
                    {expertise.technologies.map((tech, idx) => (
                      <span key={idx} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs hover:bg-[#00d690] hover:text-white transition-colors">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="text-center space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Projects:</span>
                    <span className="text-[#00d690] font-semibold">{expertise.projects}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Clients:</span>
                    <span className="text-gray-700 font-medium">{expertise.clients}</span>
                  </div>
                </div>

                <div className="text-center">
                  <button
                    className="bg-[#00d690] hover:bg-[#00b876] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 w-full"
                    onClick={() => navigateToPage(expertise.path)}
                  >
                    Learn More
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button
              className="bg-white border-2 border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white font-semibold py-3 px-8 rounded-lg transition-all duration-300"
              onClick={() => navigateToPage('/ai-ml-services')}
            >
              Explore All AI/ML Services
            </button>
          </div>
        </div>
      </section>



      {/* FAQ Section */}
      <FAQ 
        faqs={homepageFAQs}
        title="Frequently Asked Questions"
        description="Get answers to common questions about our development services, AI/ML capabilities, process, and what to expect when working with OctalChip."
      />

      {/* Client Logos Section */}
      <section id="client-logos" className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">Trusted by Growing Businesses</h2>
            <p className="text-base md:text-lg text-gray-600">We're proud to partner with innovative companies across various industries</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 items-center opacity-60">
            {clientLogos.map((client, index) => (
              <div key={index} className="text-center group hover:opacity-100 transition-opacity">
                <div className="w-16 h-16 bg-white rounded-lg flex items-center justify-center mx-auto mb-2 text-2xl shadow-sm group-hover:shadow-md transition-shadow">
                  {client.logo}
                </div>
                <p className="text-lg font-bold text-gray-800">{client.name}</p>
                <p className="text-sm font-semibold text-gray-600">{client.industry}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="text-gray-700 text-lg font-bold italic">
              "We've helped 25+ companies transform their digital presence"
            </p>
          </div>
        </div>
      </section>

      {/* Our SaaS Products Section */}
      <section id="saas-products" className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">Our SaaS Products</h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              Discover our innovative SaaS solutions designed to solve real business problems and streamline operations
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="bg-gradient-to-br from-[#00d690]/5 to-blue-500/5 rounded-2xl p-8 shadow-lg">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center">
                      <Icon name="Mail" size={24} className="text-[#00d690]" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-800">Email Validator SaaS</h3>
                      <p className="text-[#00d690] font-semibold">Professional Email Validation Service</p>
                    </div>
                  </div>

                  <p className="text-gray-600 mb-6 leading-relaxed">
                    Professional email validation service with 99.9% accuracy. Validate single emails or bulk lists
                    to reduce bounce rates and improve deliverability. Get 100 free credits with company domain signup.
                  </p>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="text-center bg-white rounded-lg p-4 shadow-sm">
                      <div className="text-2xl font-bold text-[#00d690] mb-1">99.9%</div>
                      <div className="text-sm text-gray-600">Accuracy Rate</div>
                    </div>
                    <div className="text-center bg-white rounded-lg p-4 shadow-sm">
                      <div className="text-2xl font-bold text-[#00d690] mb-1">Bulk</div>
                      <div className="text-sm text-gray-600">Processing</div>
                    </div>
                    <div className="text-center bg-white rounded-lg p-4 shadow-sm">
                      <div className="text-2xl font-bold text-[#00d690] mb-1">API</div>
                      <div className="text-sm text-gray-600">Integration</div>
                    </div>
                    <div className="text-center bg-white rounded-lg p-4 shadow-sm">
                      <div className="text-2xl font-bold text-[#00d690] mb-1">GDPR</div>
                      <div className="text-sm text-gray-600">Compliant</div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-800 mb-3">Key Features:</h4>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex items-center text-sm text-gray-600">
                        <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                        Real-time validation
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                        Bulk processing
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                        Detailed analytics
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                        API access
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <a
                      href="https://mailvalidator.octalchip.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-[#00d690] hover:bg-[#00b876] text-white px-6 py-3 rounded-lg font-semibold transition-colors text-center"
                    >
                      Try Email Validator
                    </a>
                    <a
                      href="/contact"
                      className="border-2 border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white px-6 py-3 rounded-lg font-semibold transition-colors text-center"
                    >
                      Get Custom Quote
                    </a>
                  </div>
                </div>

                <div className="text-center">
                  <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-dashed border-[#00d690]/30">
                    <div className="w-20 h-20 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Icon name="Mail" size={40} className="text-[#00d690]" />
                    </div>
                    <h4 className="text-lg md:text-xl font-bold text-gray-800 mb-4">Free Trial Available</h4>
                    <p className="text-gray-600 mb-6">
                      Get 100 free credits to test our email validation service
                    </p>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">Free Credits:</span>
                        <span className="font-semibold text-[#00d690]">100</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">Validation Type:</span>
                        <span className="font-semibold text-gray-800">Single & Bulk</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">Support:</span>
                        <span className="font-semibold text-gray-800">Email Support</span>
                      </div>
                    </div>
                    <a
                      href="https://mailvalidator.octalchip.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block w-full bg-[#00d690] hover:bg-[#00b876] text-white px-6 py-3 rounded-lg font-semibold transition-colors mt-6"
                    >
                      Start Free Trial
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Stack Section */}
      <section id="technology-stack" className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">Our Technology Stack</h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              We leverage cutting-edge technologies and frameworks including AI/ML to build robust, scalable, and maintainable solutions
              that drive your business forward.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-6">Frontend Development</h3>
              <div className="space-y-6">
                {frontendTechnologies.map((item, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-6 hover:bg-gray-100 transition-colors">
                    <div className="flex justify-between items-start mb-3">
                      <h4 className="text-lg font-semibold text-gray-800">{item.tech}</h4>
                      <span className="bg-[#00d690]/10 text-[#00d690] px-2 py-1 rounded text-xs font-medium">
                        {item.projects} projects
                      </span>
                    </div>
                    <p className="text-gray-600 mb-3">{item.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {item.benefits.map((benefit, idx) => (
                        <span key={idx} className="bg-[#00d690]/10 text-[#00d690] px-2 py-1 rounded text-xs">
                          {benefit}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-6">Backend & Infrastructure</h3>
              <div className="space-y-6">
                {backendTechnologies.map((item, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-6 hover:bg-gray-100 transition-colors">
                    <div className="flex justify-between items-start mb-3">
                      <h4 className="text-lg font-semibold text-gray-800">{item.tech}</h4>
                      <span className="bg-[#00d690]/10 text-[#00d690] px-2 py-1 rounded text-xs font-medium">
                        {item.projects} projects
                      </span>
                    </div>
                    <p className="text-gray-600 mb-3">{item.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {item.benefits.map((benefit, idx) => (
                        <span key={idx} className="bg-[#00d690]/10 text-[#00d690] px-2 py-1 rounded text-xs">
                          {benefit}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-6">AI & Machine Learning</h3>
              <div className="space-y-6">
                {aiMLTechnologies.map((item, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-6 hover:bg-gray-100 transition-colors">
                    <div className="flex justify-between items-start mb-3">
                      <h4 className="text-lg font-semibold text-gray-800">{item.tech}</h4>
                      <span className="bg-[#00d690]/10 text-[#00d690] px-2 py-1 rounded text-xs font-medium">
                        {item.projects} projects
                      </span>
                    </div>
                    <p className="text-gray-600 mb-3">{item.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {item.benefits.map((benefit, idx) => (
                        <span key={idx} className="bg-[#00d690]/10 text-[#00d690] px-2 py-1 rounded text-xs">
                          {benefit}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">Want to learn more about our technology choices?</p>
            <button
              className="bg-[#00d690] hover:bg-[#00b876] text-white font-semibold py-3 px-8 rounded-lg transition-all duration-300"
              onClick={() => navigateToPage('/services')}
            >
              View Our Tech Stack
            </button>
          </div>
        </div>
      </section>

      {/* Security & Compliance Section */}
      <section id="security-compliance" className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">Your Data Security is Our Priority</h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              We implement enterprise-grade security measures to protect your data and ensure compliance with international standards
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {securityFeatures.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow text-center">
                <div className="w-16 h-16 bg-[#00d690]/10 rounded-full flex items-center justify-center text-3xl mx-auto mb-4">
                  {item.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{item.description}</p>
                <p className="text-[#00d690] text-xs font-medium">{item.details}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-white rounded-xl p-8 shadow-lg max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">Compliance & Certifications</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Icon name="FileText" size={24} className="text-gray-600" />
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">GDPR Compliant</h4>
                <p className="text-gray-600 text-sm">European data protection standards</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Icon name="Building" size={24} className="text-gray-600" />
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">SOC 2 Type II</h4>
                <p className="text-gray-600 text-sm">Security and availability controls</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Icon name="Lock" size={24} className="text-gray-600" />
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">ISO 27001</h4>
                <p className="text-gray-600 text-sm">Information security management</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Integration Partners Section - Inspired by Verifalia */}
      <section id="integrations" className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">Integrated with Leading Platforms</h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              Seamlessly integrate with your existing tools and workflows through our comprehensive API and pre-built integrations
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 mb-12">
            {technologyPartners.map((partner, index) => (
              <div key={index} className="text-center group">
                <div className="w-16 h-16 bg-gray-50 rounded-lg flex items-center justify-center mx-auto mb-2 text-2xl group-hover:bg-[#00d690]/10 transition-colors">
                  {partner.logo}
                </div>
                <p className="text-sm font-medium text-gray-800">{partner.name}</p>
                <p className="text-xs text-gray-500">{partner.category}</p>
              </div>
            ))}
          </div>

          <div className="bg-gray-50 rounded-xl p-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="text-center lg:text-left">
                <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-4">API Integration</h3>
                <p className="text-gray-600 mb-4">
                  Our RESTful API allows seamless integration with any platform or custom application
                </p>
                <button
                  className="bg-[#00d690] hover:bg-[#00b876] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300"
                  onClick={() => window.open('https://mailvalidator.octalchip.com/docs', '_blank')}
                >
                  View API Docs
                </button>
              </div>
              <div className="text-center lg:text-left">
                <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-4">Custom Integrations</h3>
                <p className="text-gray-600 mb-4">
                  Need a specific integration? Our team can build custom connectors for your unique requirements
                </p>
                <button
                  className="bg-[#00d690] hover:bg-[#00b876] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300"
                  onClick={handleContactForm}
                >
                  Contact Us
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Development Process Timeline - Inspired by Bacancy */}
      <section id="development-timeline" className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">Our Development Journey</h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              From concept to deployment, we follow a proven methodology that ensures quality, transparency, and timely delivery
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-[#00d690]"></div>

              {developmentTimeline.map((step, index) => (
                <div key={index} className="relative flex items-start mb-12">
                  {/* Timeline Dot */}
                  <div className="absolute left-6 w-4 h-4 bg-[#00d690] rounded-full border-4 border-white shadow-lg"></div>

                  {/* Content */}
                  <div className="ml-16 bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-[#00d690]/10 rounded-full flex items-center justify-center text-xl mr-4">
                        {step.icon}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-800">{step.phase}</h3>
                        <p className="text-[#00d690] font-semibold">{step.duration}</p>
                      </div>
                    </div>

                    <p className="text-gray-600 mb-4">{step.description}</p>

                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2">Key Deliverables:</h4>
                      <div className="flex flex-wrap gap-2">
                        {step.deliverables.map((deliverable, idx) => (
                          <span key={idx} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                            {deliverable}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table Section - Inspired by Uplers */}
      <section id="comparison" className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">Why OctalChip is the Better Choice</h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              Compare our development services including AI/ML capabilities with traditional outsourcing and freelancing platforms
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800">Features</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-[#00d690]">OctalChip</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-gray-600">Outsourcing Companies</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-gray-600">Freelancing Platforms</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {comparisonFeatures.map((row, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-800">{row.feature}</td>
                        <td className="px-6 py-4 text-sm text-center text-gray-600">{row.octalchip}</td>
                        <td className="px-6 py-4 text-sm text-center text-gray-500">{row.outsourcing}</td>
                        <td className="px-6 py-4 text-sm text-center text-gray-500">{row.freelancing}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Project Calculator Section - Inspired by Verifalia */}
      <section id="calculator" className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">Project Cost Calculator</h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              Get an instant estimate for your development project based on your requirements
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-6">Project Details</h3>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Project Type</label>
                      <select
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00d690] focus:border-transparent"
                        value={calculatorState.projectType}
                        onChange={handleProjectTypeChange}
                      >
                        {projectTypes.map((type, index) => (
                          <option key={index} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Complexity Level</label>
                      <div className="space-y-2">
                        {complexityLevels.map((level, index) => (
                          <label key={index} className="flex items-center">
                            <input
                              type="radio"
                              name="complexity"
                              value={level}
                              checked={calculatorState.complexity === level}
                              onChange={handleComplexityChange}
                              className="mr-2 text-[#00d690]"
                            />
                            <span className="text-gray-700">{level}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Timeline</label>
                      <select
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00d690] focus:border-transparent"
                        value={calculatorState.timeline}
                        onChange={handleTimelineChange}
                      >
                        {timelineOptions.map((option, index) => (
                          <option key={index} value={option}>{option}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Additional Features</label>
                      <div className="space-y-2">
                        {additionalFeaturesOptions.map((feature, index) => (
                          <label key={index} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={calculatorState.additionalFeatures.includes(feature)}
                              onChange={() => handleFeatureToggle(feature)}
                              className="mr-2 text-[#00d690]"
                            />
                            <span className="text-gray-700">{feature}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-6">Estimated Cost</h3>

                  <div className="bg-gradient-to-br from-[#00d690]/10 to-[#00d690]/5 rounded-xl p-6 mb-6">
                    <div className="text-center">
                      <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#00d690] mb-2">
                        ${costBreakdown.min.toLocaleString()} - ${costBreakdown.max.toLocaleString()}
                      </div>
                      <p className="text-gray-600">Estimated project cost</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600">Base Development</span>
                      <span className="font-semibold">${costBreakdown.base.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600">Additional Features</span>
                      <span className="font-semibold">${costBreakdown.additional.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600">Testing & QA</span>
                      <span className="font-semibold">${costBreakdown.testing.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600">Deployment & Setup</span>
                      <span className="font-semibold">${costBreakdown.deployment.toLocaleString()}</span>
                    </div>
                  </div>

                  <button
                    className="w-full bg-[#00d690] hover:bg-[#00b876] text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 mt-6"
                    onClick={handleContactForm}
                  >
                    Get Detailed Quote
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Success Metrics Section */}
      <section id="success-metrics" className="py-12 md:py-20 bg-gradient-to-br from-[#00d690]/5 to-[#00d690]/10">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">Our Development Excellence</h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              Key metrics that demonstrate our commitment to delivering exceptional development services and client success.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {successMetrics.map((item, index) => (
              <div key={index} className="text-center bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#00d690] mb-2">{item.metric}</div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">{item.label}</h3>
                <p className="text-gray-600 text-sm">{item.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <div className="bg-white rounded-xl p-8 shadow-lg max-w-4xl mx-auto">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Why Clients Choose OctalChip</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Icon name="Clock" size={24} className="text-[#00d690]" />
                  </div>
                  <h4 className="font-semibold text-gray-800 mb-2">Fast Delivery</h4>
                  <p className="text-gray-600 text-sm">Agile development process ensures quick turnaround</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Icon name="Shield" size={24} className="text-[#00d690]" />
                  </div>
                  <h4 className="font-semibold text-gray-800 mb-2">Quality Assurance</h4>
                  <p className="text-gray-600 text-sm">Rigorous testing and code review processes</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Icon name="Users" size={24} className="text-[#00d690]" />
                  </div>
                  <h4 className="font-semibold text-gray-800 mb-2">Expert Team</h4>
                  <p className="text-gray-600 text-sm">Experienced developers with proven track records</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Expertise Section - Inspired by Bacancy */}
      <section id="industry-expertise" className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">Industry-Specific Solutions</h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              We understand the unique challenges of different industries and deliver tailored solutions that drive growth
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industrySolutions.map((item, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-100 group">
                <div className="text-center mb-4">
                  <div className="w-16 h-16 bg-[#00d690]/10 rounded-full flex items-center justify-center text-3xl mx-auto mb-4 group-hover:bg-[#00d690] group-hover:text-white transition-all duration-300">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-[#00d690] transition-colors">{item.industry}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                </div>

                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Key Features:</h4>
                  <div className="flex flex-wrap gap-1">
                    {item.features.map((feature, idx) => (
                      <span key={idx} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Technologies:</h4>
                  <div className="flex flex-wrap gap-1">
                    {item.technologies.map((tech, idx) => (
                      <span key={idx} className="bg-[#00d690]/10 text-[#00d690] px-2 py-1 rounded text-xs">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex justify-between items-center text-sm text-gray-500 mb-4">
                  <span>Projects: <span className="font-semibold text-[#00d690]">{item.projects}</span></span>
                </div>

                <button
                  className="w-full bg-[#00d690] hover:bg-[#00b876] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300"
                  onClick={() => navigateToPage('/services')}
                >
                  Learn More
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI/ML Industry-Specific Solutions Section */}
      <section id="ai-ml-industry" className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">AI/ML Industry-Specific Solutions</h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              Leverage the power of AI and Machine Learning to solve industry-specific challenges and unlock new opportunities for growth
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {aiMLIndustrySolutions.map((item, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-100 group">
                <div className="text-center mb-4">
                  <div className="w-16 h-16 bg-[#00d690]/10 rounded-full flex items-center justify-center text-3xl mx-auto mb-4 group-hover:bg-[#00d690] group-hover:text-white transition-all duration-300">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-[#00d690] transition-colors">{item.industry}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                </div>

                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Key Features:</h4>
                  <div className="flex flex-wrap gap-1">
                    {item.features.map((feature, idx) => (
                      <span key={idx} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Technologies:</h4>
                  <div className="flex flex-wrap gap-1">
                    {item.technologies.map((tech, idx) => (
                      <span key={idx} className="bg-[#00d690]/10 text-[#00d690] px-2 py-1 rounded text-xs">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex justify-between items-center text-sm text-gray-500 mb-4">
                  <span>Projects: <span className="font-semibold text-[#00d690]">{item.projects}</span></span>
                </div>

                <button
                  className="w-full bg-[#00d690] hover:bg-[#00b876] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300"
                  onClick={() => navigateToPage('/ai-ml-services')}
                >
                  Learn More
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Global Reach Section - Inspired by Uplers */}
      <section id="global-reach" className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">Serving Clients Globally</h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              From startups to enterprises, we deliver exceptional development solutions across different time zones and markets
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-6">Our Global Presence</h3>
              <div className="space-y-4">
                {globalRegions.map((region, index) => (
                  <div key={index} className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-semibold text-gray-800">{region.region}</h4>
                        <p className="text-sm text-gray-600">{region.focus}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-[#00d690] font-bold">{region.clients}</div>
                        <div className="text-xs text-gray-500">{region.timezone}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-6">Why Global Clients Choose Us</h3>
              <div className="space-y-4">
                {globalBenefits.map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-10 h-10 bg-[#00d690]/10 rounded-full flex items-center justify-center text-lg flex-shrink-0">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-1">{item.title}</h4>
                      <p className="text-gray-600 text-sm">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section id="contact" style={{ backgroundColor: '#f1f5f9', padding: '80px 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <h2 style={{ fontSize: '36px', fontWeight: 'bold', color: '#1E293B', marginBottom: '24px' }}>
              Ready to Start Your Project?
            </h2>
            <p style={{ fontSize: '20px', color: '#64748B', marginBottom: '32px' }}>
              Let's discuss your ideas and turn them into reality. Contact us today for a free consultation.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
              <button
                style={{
                  backgroundColor: '#00d690',
                  color: 'white',
                  padding: '16px 32px',
                  borderRadius: '8px',
                  fontSize: '18px',
                  fontWeight: '600',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#00b876'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#00d690'}
                onClick={() => window.open('https://cal.com/octalchip/30min', '_blank')}
              >
                Book a 30 Min Call
              </button>
              <button
                style={{
                  backgroundColor: 'transparent',
                  color: '#00d690',
                  padding: '16px 32px',
                  borderRadius: '8px',
                  fontSize: '18px',
                  fontWeight: '600',
                  border: '2px solid #00d690',
                  cursor: 'pointer',
                  transition: 'all 0.3s'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#00d690';
                  e.target.style.color = 'white';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#00d690';
                }}
                onClick={() => navigateToPage('/services')}
              >
                View Our Services
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home; 